import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import api from '../services/api';
import { updateInvoice, addInvoiceItem, editInvoiceItem, deleteInvoiceItem, reviewInvoice, sendInvoice, syncInvoice, getInvoiceRecipients, getInvoiceEmails } from '../services/quickbooksService';
import { bolAPI } from '../services/millAPI';
import { formatDisplayDate } from '../utils/dateUtils';
import PrintableInvoice from '../components/Portal/PrintableInvoice';
import PrintablePackingSlip from '../components/Portal/PrintablePackingSlip';
import './InvoiceDetail.css';

interface LineItem {
  id?: number;
  line_number: number;
  feed_name: string;
  quantity: number;
  unit: string;  // 'bags', 'sacks', 'lbs'
  quantity_display: string;  // "50 bags" (Phase 3 unified)
  description_display?: string;  // "Essential Layer 17 (50# Mash)" — embeds size + form
  quantity_lbs: string;  // Total pounds (for reference)
  unit_price: string;
  unit_price_display: string;  // "$22.90/bag" or "$0.57/lb"
  unit_price_per_lb?: string;
  price_per_package?: string;
  number_of_packages?: number;
  total_quantity_lbs?: string;
  pricing_display_method?: 'PER_LB' | 'PER_PACKAGE';
  line_total: string;
  package_type: string;
  is_pelleted: boolean;
  is_promo?: boolean;
  promo_reason?: string;
  campaign_enrollment_id?: number | null;
  campaign_name?: string;
}

interface Payment {
  id: number;
  amount: string;
  payment_method: string;
  payment_date: string;
  reference_number: string;
  qb_payment_id?: string | null;
  qb_payment_synced?: boolean;
  helcim_transaction_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  recorded_by_name: string | null;
  voided?: boolean;
  voided_at?: string | null;
  voided_by_name?: string | null;
  void_reason?: string;
  qb_void_synced?: boolean;
}

interface Customer {
  id: number;
  name: string;
  contact_person: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  postal_code: string;
  payment_terms: string;
}

interface Order {
  id: number;
  order_number: string;
  order_date: string;
  delivery_date: string | null;
  status: string;
  shipping_method: string;
  notes: string;
  special_instructions: string;
  liftgate_required?: boolean;
  // Tracking availability (usually empty until the order ships) —
  // drives the "send with tracking" toggle hint.
  pro_number?: string;
  tracking_event_count?: number;
}

// The ONE BOL for this invoice's order — same record the mill shipping
// side makes/reads (GET /mill/bol/for_order/). Never a parallel copy.
interface BOLInfo {
  id: number;
  bol_number: string;
  carrier_display: string;
  ship_date: string;
  status: string;
  status_display?: string;
  created_by?: string | null;
  created_at?: string;
  is_stale?: boolean;
  has_pdf?: boolean;
  quote_number?: string;
  total_pallets?: number;
  total_weight_lbs?: number;
  driver_name?: string;
  trailer_number?: string;
  seal_number?: string;
  // Quoted (authoritative-when-quote-sourced) numbers + pack-time discrepancy.
  is_quote_sourced?: boolean;
  quoted_pallet_count?: number | null;
  quoted_weight_lbs?: number | null;
  display_pallet_count?: number;
  display_weight_lbs?: number;
  pallet_count_discrepancy?: number | null;
  // Pallet-build override: the flag + the current per-row build (seeds the editor).
  pallets_manually_edited?: boolean;
  line_items?: BOLLineItem[];
  // Editable header/reference/carrier/routing/consignee fields + special services.
  pro_number?: string;
  pickup_number?: string;
  customer_po_number?: string;
  reference_number?: string;
  estimated_delivery?: string;
  carrier?: string;
  carrier_name?: string;
  consignee_name?: string;
  consignee_address?: string;
  consignee_city?: string;
  consignee_state?: string;
  consignee_zip?: string;
  consignee_phone?: string;
  special_instructions?: string;
  liftgate_required?: boolean;
  residential_delivery?: boolean;
  inside_delivery?: boolean;
  appointment_required?: boolean;
  notify_before_delivery?: boolean;
  // Manual-edit guards → drive the "edited" indicators.
  consignee_manually_edited?: boolean;
  liftgate_manually_edited?: boolean;
  ship_date_manually_edited?: boolean;
  quote_number_manually_edited?: boolean;
  customer_po_number_manually_edited?: boolean;
  // Void & reissue lineage.
  reissued_from?: number | null;
  reissued_from_bol_number?: string | null;
}

interface BOLLineItem {
  description: string;
  number_of_pallets: number;
  weight_lbs: number;
  package_type?: string;
  nmfc_code?: string;
  freight_class?: string;
}

// Standard NMFC freight classes (must match the backend allow-list). Freight class
// is a SELECT, never free text — a wrong class misrates the freight.
const FREIGHT_CLASSES = ['50', '55', '60', '65', '70', '77.5', '85', '92.5', '100',
  '110', '125', '150', '175', '200', '250', '300', '400', '500'];

// Small marker on a BOL field whose *_manually_edited flag is set — so staff can
// see at a glance what's been hand-overridden vs autofilled.
const EditedChip: React.FC = () => (
  <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 8, background: '#dcfce7', color: '#166534', fontSize: 10, fontWeight: 700 }}>
    edited
  </span>
);

interface CreditApplicationEntry {
  amount_applied: string;
  applied_at: string | null;
  credit_type: string;
  credit_type_display: string;
  reference: string;
}

interface Invoice {
  id: number;
  invoice_number: string;
  order_number: string;
  customer_name: string;
  customer_id: number | null;
  status: string;
  subtotal: string;
  shipping_cost: string;
  tax: string;
  total: string;
  paid_amount: string;
  credits_applied: string;
  customer_credits_applied?: string;
  credit_applications?: CreditApplicationEntry[];
  balance_due: string;
  due_date: string | null;
  is_overdue: boolean;
  payment_link: string | null;
  needs_qb_sync: boolean;
  qb_invoice_id: string | null;
  shipping_override: string | null;
  shipping_override_reason: string | null;
  shipping_quote_number: string | null;
  promo_discount_amount: string;
  discount_amount: string;
  discount_reason: string;
  discount_category_id: number | null;
  discount_category_name: string;
  discount_notes: string;
  shipping_discount: string;
  shipping_discount_reason: string;
  notes: string;
  customer_notes: string;
  created_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  sent_at: string | null;
  paid_at: string | null;
  // Drives the dynamic Send/Resend button label and the "customer
  // emailed X ago" indicator. Stamped by invoice_review (first approve)
  // and invoice_send (every successful send).
  last_emailed_at: string | null;
  // Drives the "synced to Helcim X ago" indicator. Stamped by
  // _ensure_helcim_invoice_setup, _maybe_sync_helcim_after_edit,
  // and the manual sync_invoice_balance management command.
  helcim_balance_synced_at: string | null;
  items: LineItem[];
  payments: Payment[];
  customer: Customer | null;
  order: Order | null;
  // Soft-delete audit fields — populated when the invoice is in the
  // Deleted tab. Used to render the "this invoice has been deleted"
  // banner + Restore action.
  is_deleted?: boolean;
  deleted_at?: string | null;
  deleted_by_name?: string | null;
  deletion_reason?: string;
}

interface EditData {
  shipping_cost: string;
  tax: string;
  due_date: string;
  notes: string;
  customer_notes: string;
  discount_amount: string;
  discount_reason: string;
  discount_category: number | null;
  discount_notes: string;
  shipping_discount: string;
  shipping_discount_reason: string;
}

interface DiscountCategory {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
  is_custom: boolean;
}

interface EditItemData {
  unit_price_per_lb: string;
  price_per_package: string;
  total_quantity_lbs: string;
  number_of_packages: string;
  is_promo: boolean;
  promo_reason: string;
  campaign_enrollment: number | null;
}

interface CustomerCampaign {
  enrollment_id: number;
  campaign_id: number;
  campaign_name: string;
  campaign_type: string;
}

interface FormulaOption {
  id: number;
  name: string;
  animal_type: string;
  default_form: string;
  available_forms: string[];
  default_bag_size: number | null;
  bag_size: number | string;
  price_bag_mash: number | string | null;
  price_bag_pellet: number | string | null;
  price_byoc_mash: number | string | null;
  price_byoc_pellet: number | string | null;
  price_1000lb_mash: number | string | null;
  price_1000lb_pellet: number | string | null;
  is_active: boolean;
}

interface CatalogProduct {
  id: number;
  name: string;
  product_type: string;
  default_price: string;
  unit: string;
  is_active: boolean;
}

interface NewLineItem {
  formula_id: number | null;
  product_id: number | null;
  item_mode: 'feed' | 'product';
  form: string;
  quantity: number;
  price_per_unit: string;
  bag_size: number;
  is_promo: boolean;
  promo_reason: string;
  campaign_enrollment: number | null;
}

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  draft: { bg: '#f3f4f6', text: '#6b7280' },
  pending_review: { bg: '#fef3c7', text: '#92400e' },
  approved: { bg: '#dbeafe', text: '#1e40af' },
  sent: { bg: '#dbeafe', text: '#1e40af' },
  partial: { bg: '#fce7f3', text: '#9d174d' },
  paid: { bg: '#d1fae5', text: '#065f46' },
  void: { bg: '#fee2e2', text: '#991b1b' },
  overdue: { bg: '#fee2e2', text: '#991b1b' },
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  pending_review: 'Needs Review',
  approved: 'Approved',
  sent: 'Sent',
  partial: 'Partial',
  paid: 'Paid',
  void: 'Void',
};

const SENT_STATUSES = ['sent', 'paid', 'partially_paid'];

const InvoiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Back-navigation context. CustomerBillingPanel sends both a URL param
  // (canonical, survives reload) and location.state (fallback skipped on
  // hard refresh). Read URL first, state second.
  const fromCustomer = searchParams.get('from') === 'customer';
  const returnToFromUrl = searchParams.get('returnTo');
  const returnToFromState = (location.state as { fromCustomerReturnPath?: string } | null)?.fromCustomerReturnPath;
  const customerReturnPath = returnToFromUrl || returnToFromState;
  const backTarget = fromCustomer && customerReturnPath ? customerReturnPath : '/sales/invoices';
  const backLabel = fromCustomer && customerReturnPath ? 'Back to Customer' : 'Back to Invoices';

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('check');
  const [paymentReference, setPaymentReference] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);
  const [showPrintView, setShowPrintView] = useState(false);
  const [fullInvoiceData, setFullInvoiceData] = useState<any>(null);
  const [printLoading, setPrintLoading] = useState(false);
  const [showPackingSlip, setShowPackingSlip] = useState(false);
  const [packingSlipData, setPackingSlipData] = useState<any>(null);
  const [packingSlipLoading, setPackingSlipLoading] = useState(false);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<EditData>({ shipping_cost: '', tax: '', due_date: '', notes: '', customer_notes: '', discount_amount: '', discount_reason: '', discount_category: null, discount_notes: '', shipping_discount: '', shipping_discount_reason: '' });
  const [discountCategories, setDiscountCategories] = useState<DiscountCategory[]>([]);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editItemData, setEditItemData] = useState<EditItemData>({ unit_price_per_lb: '', price_per_package: '', total_quantity_lbs: '', number_of_packages: '', is_promo: false, promo_reason: '', campaign_enrollment: null });
  const [saving, setSaving] = useState(false);

  // Shipping override state
  const [editingShippingOverride, setEditingShippingOverride] = useState(false);
  const [shippingOverride, setShippingOverride] = useState('');
  const [shippingOverrideReason, setShippingOverrideReason] = useState('');

  // Soft-delete / restore state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deletingInvoice, setDeletingInvoice] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [restoringInvoice, setRestoringInvoice] = useState(false);
  const [restoreErrorMsg, setRestoreErrorMsg] = useState<string | null>(null);

  // Void Payment + Unmark Paid state
  const [voidingPayment, setVoidingPayment] = useState<Payment | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [voidConfirmChecked, setVoidConfirmChecked] = useState(false);
  const [voidLoading, setVoidLoading] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);
  const [showUnmarkPaidModal, setShowUnmarkPaidModal] = useState(false);
  const [unmarkReason, setUnmarkReason] = useState('');
  const [unmarkConfirmChecked, setUnmarkConfirmChecked] = useState(false);
  const [unmarkLoading, setUnmarkLoading] = useState(false);
  const [unmarkError, setUnmarkError] = useState<string | null>(null);
  // Persistent yellow banner shown when a QB sync (record or void) fails.
  // Auto-dismisses after 8s OR the user clicks X.
  const [qbSyncWarning, setQbSyncWarning] = useState<string | null>(null);
  const [savingShippingOverride, setSavingShippingOverride] = useState(false);

  // Shipping quote number state
  const [shippingQuoteNumber, setShippingQuoteNumber] = useState('');
  const [savingQuoteNumber, setSavingQuoteNumber] = useState(false);

  // BOL state — the ONE BOL shared with the mill shipping side.
  const [bolInfo, setBolInfo] = useState<BOLInfo | null>(null);
  const [loadingBol, setLoadingBol] = useState(false);
  const [showBolCreateModal, setShowBolCreateModal] = useState(false);
  const [bolTrackingNumber, setBolTrackingNumber] = useState('');
  const [bolCarrier, setBolCarrier] = useState('FASTWAY');
  const [bolShipDate, setBolShipDate] = useState('');
  const [bolWorking, setBolWorking] = useState(false);
  // Field parity with the mill builder + Sibyl's quoted numbers.
  const [bolQuoteNumber, setBolQuoteNumber] = useState('');
  const [bolDriverName, setBolDriverName] = useState('');
  const [bolTrailerNumber, setBolTrailerNumber] = useState('');
  const [bolSpecialInstructions, setBolSpecialInstructions] = useState('');
  const [bolPickupNumber, setBolPickupNumber] = useState('');
  // Special services — print on the BOL. Liftgate pre-fills from the order but is
  // overridable; the other four start unchecked (they have no order source).
  const [bolLiftgate, setBolLiftgate] = useState(false);
  const [bolResidential, setBolResidential] = useState(false);
  const [bolInside, setBolInside] = useState(false);
  const [bolAppointment, setBolAppointment] = useState(false);
  const [bolNotify, setBolNotify] = useState(false);
  const [bolPalletCount, setBolPalletCount] = useState('');
  const [bolWeight, setBolWeight] = useState('');
  // Re-quote mode: replace the existing (discrepant) BOL with a corrected quote.
  const [bolReplaceMode, setBolReplaceMode] = useState(false);
  // Pallet-build override editor: a list of PHYSICAL pallets (one row each) Brad is
  // actually shipping — he combines/splits/adds/removes here and it prints on the BOL.
  const [showPalletEditor, setShowPalletEditor] = useState(false);
  const [palletBuild, setPalletBuild] = useState<{ weight: string; description: string; nmfc: string; freightClass: string }[]>([]);
  const [palletBuildNote, setPalletBuildNote] = useState<string | null>(null);
  const [palletOriginalWeight, setPalletOriginalWeight] = useState(0);
  // BOL details editor (header/reference, consignee, carrier & routing, services,
  // instructions) — every printed field editable after the fact via PATCH.
  const [showBolEditor, setShowBolEditor] = useState(false);
  const [bolEdit, setBolEdit] = useState<Record<string, string | boolean>>({});
  // Void & reissue (post-shipment correction) dialog.
  const [showReissue, setShowReissue] = useState(false);
  const [reissueReason, setReissueReason] = useState('');
  const [reissueNumber, setReissueNumber] = useState('');

  // Payment link state
  const [editingPaymentLink, setEditingPaymentLink] = useState(false);
  const [paymentLink, setPaymentLink] = useState('');
  const [savingPaymentLink, setSavingPaymentLink] = useState(false);

  // Review state — shipping is deliberately NOT part of the review flow
  // anymore (the old blank-defaults-to-0 box zeroed real shipping on
  // approve). Shipping is edited only via the inline Edit-mode field.
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(false);

  // Persistent yellow Helcim-sync-failed banner. Set when ANY action
  // (Save Changes / Save line item / Approve & Update) returns
  // helcim_sync='failed' in the response. Dismissed only by a successful
  // Retry Helcim Sync — never by user click — so Sibyl can't dismiss
  // and forget the drift.
  const [helcimWarning, setHelcimWarning] = useState<{ error: string } | null>(null);
  const [retryingHelcimSync, setRetryingHelcimSync] = useState(false);

  // Send modal state
  const [showSendModal, setShowSendModal] = useState(false);
  // Per-invoice send choice: false = invoice only; true = invoice +
  // the order's tracking section. If tracking doesn't exist yet the
  // backend answers 409 no_tracking and we WARN (no queue, no auto-send).
  const [sendWithTracking, setSendWithTracking] = useState(false);
  const [recipientOptions, setRecipientOptions] = useState<{id: number; email: string; name: string; role: string}[]>([]);
  const [selectedRecipients, setSelectedRecipients] = useState<Set<string>>(new Set());
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [loadingRecipients, setLoadingRecipients] = useState(false);

  // Email log state
  const [emailLogs, setEmailLogs] = useState<{recipient_email: string; status: string; sent_at: string | null}[]>([]);

  // Add line item state
  const [showAddItem, setShowAddItem] = useState(false);
  const [formulas, setFormulas] = useState<FormulaOption[]>([]);
  const [formulaSearch, setFormulaSearch] = useState('');
  const [newItem, setNewItem] = useState<NewLineItem>({
    formula_id: null, product_id: null, item_mode: 'feed',
    form: 'MASH', quantity: 1, price_per_unit: '', bag_size: 40,
    is_promo: false, promo_reason: '', campaign_enrollment: null,
  });
  const [addingItem, setAddingItem] = useState(false);
  const [customerCampaigns, setCustomerCampaigns] = useState<CustomerCampaign[]>([]);
  const [catalogProducts, setCatalogProducts] = useState<CatalogProduct[]>([]);
  const [productSearch, setProductSearch] = useState('');

  useEffect(() => {
    loadInvoice();
    api.get('/discount-categories/').then(res => setDiscountCategories(res.data)).catch(() => {});
  }, [id]);

  // Load email logs when invoice loads in sent/paid status
  useEffect(() => {
    if (invoice && ['sent', 'paid', 'partial', 'partially_paid'].includes(invoice.status)) {
      loadEmailLogs();
    }
  }, [invoice?.id, invoice?.status]);

  const loadInvoice = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/invoices/${id}/`);
      const inv = response.data;
      setInvoice(inv);
      setPaymentLink(inv.payment_link || '');
      setShippingOverride(inv.shipping_override || '');
      setShippingOverrideReason(inv.shipping_override_reason || '');
      setShippingQuoteNumber(inv.shipping_quote_number || '');
      // Load customer campaigns for promo linking
      if (inv.customer_id) {
        api.get('/promo-campaigns/customer-campaigns/', { params: { customer_id: inv.customer_id } })
          .then(res => setCustomerCampaigns(res.data || []))
          .catch(() => {});
      }
    } catch (err: any) {
      console.error('Error loading invoice:', err);
      setError(err.response?.data?.error || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const loadFormulas = async () => {
    try {
      const response = await api.get('/feed-formulas/', { params: { is_active: true } });
      setFormulas(response.data || []);
    } catch (err) {
      console.error('Error loading formulas:', err);
    }
    try {
      const res = await api.get('/products/', { params: { is_active: true } });
      setCatalogProducts((res.data || []).filter((p: CatalogProduct) => p.product_type !== 'feed'));
    } catch (err) {
      console.error('Error loading products:', err);
    }
  };

  const getSelectedFormula = (): FormulaOption | null => {
    if (!newItem.formula_id) return null;
    return formulas.find(f => f.id === newItem.formula_id) || null;
  };

  const getAutoPrice = (formula: FormulaOption, form: string): string => {
    // For bags, use bag pricing
    if (form === 'PELLET' && formula.price_bag_pellet) return String(formula.price_bag_pellet);
    if (form === 'MASH' && formula.price_bag_mash) return String(formula.price_bag_mash);
    if (formula.price_bag_pellet) return String(formula.price_bag_pellet);
    if (formula.price_bag_mash) return String(formula.price_bag_mash);
    return '';
  };

  const handleFormulaSelect = (formulaId: number) => {
    const formula = formulas.find(f => f.id === formulaId);
    if (!formula) return;

    const defaultForm = formula.default_form || (formula.available_forms?.length === 1 ? formula.available_forms[0] : 'MASH');
    const bagSize = parseInt(String(formula.default_bag_size || formula.bag_size || 40)) || 40;
    const price = getAutoPrice(formula, defaultForm);

    setNewItem(prev => ({
      ...prev,
      formula_id: formulaId,
      form: defaultForm,
      quantity: 1,
      price_per_unit: price,
      bag_size: bagSize,
    }));
  };

  const handleAddItemFormChange = (form: string) => {
    const formula = getSelectedFormula();
    const price = formula ? getAutoPrice(formula, form) : '';
    setNewItem(prev => ({ ...prev, form, price_per_unit: price }));
  };

  const handleAddItem = async () => {
    if (!invoice || !newItem.price_per_unit) return;
    if (newItem.item_mode === 'feed' && !newItem.formula_id) return;
    if (newItem.item_mode === 'product' && !newItem.product_id) return;
    setAddingItem(true);
    setMessage(null);
    try {
      let updated;
      if (newItem.item_mode === 'product' && newItem.product_id) {
        // Non-feed product: simple quantity × price
        updated = await addInvoiceItem(invoice.id, {
          product_id: newItem.product_id,
          quantity: newItem.quantity,
          price_per_unit: parseFloat(newItem.price_per_unit),
          is_promo: newItem.is_promo,
          promo_reason: newItem.promo_reason,
          campaign_enrollment: newItem.campaign_enrollment,
        });
      } else {
        // Feed formula: existing bag-based pricing
        const isPellet = newItem.form === 'PELLET';
        const totalLbs = newItem.quantity * newItem.bag_size;
        const pricePerLb = parseFloat(newItem.price_per_unit) / newItem.bag_size;

        updated = await addInvoiceItem(invoice.id, {
          formula_id: newItem.formula_id!,
          package_type: newItem.bag_size <= 19 ? 'BAGS_19' : 'BAGS',
          total_quantity_lbs: totalLbs,
          number_of_packages: newItem.quantity,
          unit_price_per_lb: pricePerLb,
          price_per_package: parseFloat(newItem.price_per_unit),
          is_pelleted: isPellet,
          is_promo: newItem.is_promo,
          promo_reason: newItem.promo_reason,
          campaign_enrollment: newItem.campaign_enrollment,
        });
      }
      setInvoice(updated);
      setNewItem({ formula_id: null, product_id: null, item_mode: 'feed', form: 'MASH', quantity: 1, price_per_unit: '', bag_size: 40, is_promo: false, promo_reason: '', campaign_enrollment: null });
      setFormulaSearch('');
      setProductSearch('');
      setShowAddItem(false);
      setMessage({ type: 'success', text: 'Line item added!' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || err.message || 'Failed to add item' });
    } finally {
      setAddingItem(false);
    }
  };

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(num || 0);
  };

  // Delegates to the shared helper so date-only fields (payment_date, due_date,
  // sent_at when date-only, etc.) render on the correct calendar day. See
  // formatDisplayDate in utils/dateUtils.
  const formatDate = (dateStr: string | null) => formatDisplayDate(dateStr);

  // "X minutes ago" / "X hours ago" / "X days ago" — used by the
  // Helcim-synced indicator and the customer-emailed indicator near
  // the payment link area. Falls back to absolute date for >30 days.
  const relativeTime = (dateStr: string | null): string => {
    if (!dateStr) return 'never';
    const then = new Date(dateStr).getTime();
    const now = Date.now();
    const diffSec = Math.max(0, Math.floor((now - then) / 1000));
    if (diffSec < 60) return 'just now';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 30) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
    return formatDate(dateStr);
  };

  const formatPaymentTerms = (terms: string | null) => {
    if (!terms) return '—';
    const termsMap: Record<string, string> = {
      'due_on_receipt': 'Due on Receipt',
      'net_10': 'Net 10 Days',
      'net_15': 'Net 15 Days',
      'net_30': 'Net 30 Days',
      'net_45': 'Net 45 Days',
      'net_60': 'Net 60 Days',
    };
    return termsMap[terms] || terms.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getStatusDisplay = () => {
    if (!invoice) return { label: '', bg: '', text: '' };
    if (invoice.is_overdue && invoice.status !== 'paid') {
      return { label: 'Overdue', ...STATUS_COLORS.overdue };
    }
    const colors = STATUS_COLORS[invoice.status] || STATUS_COLORS.draft;
    const label = STATUS_LABELS[invoice.status] || invoice.status;
    return { label, ...colors };
  };

  // Edit mode helpers
  const canEdit = !!invoice;
  const showSentWarning = invoice && SENT_STATUSES.includes(invoice.status);

  const handleEnterEdit = () => {
    if (!invoice) return;
    setEditData({
      shipping_cost: invoice.shipping_cost,
      tax: invoice.tax,
      due_date: invoice.due_date || '',
      notes: invoice.notes || '',
      customer_notes: invoice.customer_notes || '',
      discount_amount: invoice.discount_amount || '0.00',
      discount_reason: invoice.discount_reason || '',
      discount_category: invoice.discount_category_id,
      discount_notes: invoice.discount_notes || '',
      shipping_discount: invoice.shipping_discount || '0.00',
      shipping_discount_reason: invoice.shipping_discount_reason || '',
    });
    setIsEditing(true);
    if (showSentWarning) {
      setMessage({ type: 'warning', text: 'This invoice has already been sent to the customer. Changes will not be reflected in the customer\'s email.' });
    } else {
      setMessage(null);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingItemId(null);
    setEditItemData({ unit_price_per_lb: '', price_per_package: '', total_quantity_lbs: '', number_of_packages: '', is_promo: false, promo_reason: '', campaign_enrollment: null });
    setEditData({ shipping_cost: '', tax: '', due_date: '', notes: '', customer_notes: '', discount_amount: '', discount_reason: '', discount_category: null, discount_notes: '', shipping_discount: '', shipping_discount_reason: '' });
  };

  const handleSaveEdits = async () => {
    if (!invoice) return;
    setSaving(true);
    setMessage(null);
    try {
      const payload: Record<string, string> = {};
      if (editData.shipping_cost !== invoice.shipping_cost) payload.shipping_cost = editData.shipping_cost;
      if (editData.tax !== invoice.tax) payload.tax = editData.tax;
      if (editData.due_date !== (invoice.due_date || '')) payload.due_date = editData.due_date;
      if (editData.notes !== (invoice.notes || '')) payload.notes = editData.notes;
      if (editData.customer_notes !== (invoice.customer_notes || '')) payload.customer_notes = editData.customer_notes;
      if (editData.discount_amount !== (invoice.discount_amount || '0.00')) payload.discount_amount = editData.discount_amount;
      if (editData.discount_category !== invoice.discount_category_id) (payload as any).discount_category = editData.discount_category;
      if (editData.discount_notes !== (invoice.discount_notes || '')) payload.discount_notes = editData.discount_notes;
      if (editData.shipping_discount !== (invoice.shipping_discount || '0.00')) payload.shipping_discount = editData.shipping_discount;
      if (editData.shipping_discount_reason !== (invoice.shipping_discount_reason || '')) payload.shipping_discount_reason = editData.shipping_discount_reason;

      if (Object.keys(payload).length === 0) {
        setIsEditing(false);
        return;
      }

      const updated = await updateInvoice(invoice.id, payload) as any;
      setInvoice(updated);
      setIsEditing(false);
      // PATCH response includes helcim_sync ('ok'/'failed'/'skipped') when
      // a sync was triggered. 'failed' raises a persistent banner; 'ok'
      // and 'skipped' clear any prior banner.
      const sync = updated?.helcim_sync;
      if (sync === 'failed') {
        setHelcimWarning({ error: updated.helcim_error || 'unknown error' });
        // No toast — the yellow banner is the durable signal so
        // Sibyl can't dismiss-and-forget the drift.
      } else if (sync === 'ok') {
        setHelcimWarning(null);
        setMessage({ type: 'success', text: 'Saved and synced to Helcim' });
      } else {
        setHelcimWarning(null);
        setMessage({ type: 'success', text: 'Saved' });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || err.message || 'Failed to save changes' });
    } finally {
      setSaving(false);
    }
  };

  const isPackagePriced = (item: LineItem) =>
    item.unit === 'bags' || item.unit === 'sacks' || item.pricing_display_method === 'PER_PACKAGE';

  const handleEditItem = (item: LineItem) => {
    if (!item.id) return;
    setEditingItemId(item.id);
    const promoFields = {
      is_promo: item.is_promo || false,
      promo_reason: item.promo_reason || '',
      campaign_enrollment: item.campaign_enrollment_id || null,
    };
    if (isPackagePriced(item)) {
      setEditItemData({
        unit_price_per_lb: item.unit_price_per_lb || '',
        price_per_package: item.price_per_package || item.unit_price || '',
        total_quantity_lbs: item.total_quantity_lbs || item.quantity_lbs || '',
        number_of_packages: item.number_of_packages != null ? String(item.number_of_packages) : String(item.quantity || ''),
        ...promoFields,
      });
    } else {
      setEditItemData({
        unit_price_per_lb: item.unit_price_per_lb || item.unit_price || '',
        price_per_package: item.price_per_package || '',
        total_quantity_lbs: item.total_quantity_lbs || item.quantity_lbs || '',
        number_of_packages: item.number_of_packages != null ? String(item.number_of_packages) : '',
        ...promoFields,
      });
    }
  };

  const handleCancelItemEdit = () => {
    setEditingItemId(null);
    setEditItemData({ unit_price_per_lb: '', price_per_package: '', total_quantity_lbs: '', number_of_packages: '', is_promo: false, promo_reason: '', campaign_enrollment: null });
  };

  const handleSaveItem = async () => {
    if (!invoice || !editingItemId) return;
    setSaving(true);
    setMessage(null);
    try {
      const editingItem = (invoice.items || []).find(i => i.id === editingItemId);
      const isPkgPricing = editingItem ? isPackagePriced(editingItem) : false;

      const payload: Record<string, string | number | boolean | null> = {};
      if (isPkgPricing) {
        if (editItemData.price_per_package) payload.price_per_package = editItemData.price_per_package;
        if (editItemData.number_of_packages) payload.number_of_packages = parseInt(editItemData.number_of_packages);
      } else {
        if (editItemData.unit_price_per_lb) payload.unit_price_per_lb = editItemData.unit_price_per_lb;
        if (editItemData.total_quantity_lbs) payload.total_quantity_lbs = editItemData.total_quantity_lbs;
      }
      // Always send promo/campaign fields
      payload.is_promo = editItemData.is_promo;
      payload.promo_reason = editItemData.promo_reason;
      payload.campaign_enrollment = editItemData.campaign_enrollment;

      const updated = await editInvoiceItem(invoice.id, editingItemId, payload) as any;
      setInvoice(updated);
      setEditingItemId(null);
      setEditItemData({ unit_price_per_lb: '', price_per_package: '', total_quantity_lbs: '', number_of_packages: '', is_promo: false, promo_reason: '', campaign_enrollment: null });
      const sync = updated?.helcim_sync;
      if (sync === 'failed') {
        setHelcimWarning({ error: updated.helcim_error || 'unknown error' });
      } else if (sync === 'ok') {
        setHelcimWarning(null);
        setMessage({ type: 'success', text: 'Saved and synced to Helcim' });
      } else {
        setHelcimWarning(null);
        setMessage({ type: 'success', text: 'Line item updated' });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || err.message || 'Failed to update item' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (item: LineItem) => {
    if (!invoice || !item.id) return;
    if (!window.confirm(`Delete "${item.feed_name}" from this invoice?`)) return;
    setSaving(true);
    setMessage(null);
    try {
      const updated = await deleteInvoiceItem(invoice.id, item.id);
      setInvoice(updated);
      setMessage({ type: 'success', text: 'Line item removed.' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || err.message || 'Failed to delete item' });
    } finally {
      setSaving(false);
    }
  };

  // Compute promo discount from line items (handles old $0 line_total data)
  const computedPromoDiscount = invoice ? (invoice.items || []).reduce((sum, item) => {
    if (!item.is_promo) return sum;
    const lt = parseFloat(item.line_total || '0');
    if (lt > 0) return sum + lt;
    // Old data: compute from price fields
    const isPkg = item.unit === 'bags' || item.unit === 'sacks' || item.pricing_display_method === 'PER_PACKAGE';
    if (isPkg && item.price_per_package && item.number_of_packages) {
      return sum + parseFloat(item.price_per_package) * item.number_of_packages;
    } else if (item.unit_price_per_lb && item.total_quantity_lbs) {
      return sum + parseFloat(item.unit_price_per_lb) * parseFloat(item.total_quantity_lbs);
    }
    return sum;
  }, 0) : 0;

  // Use backend promo_discount_amount if it's > 0, otherwise use computed
  const effectivePromoDiscount = invoice
    ? (parseFloat(invoice.promo_discount_amount || '0') > 0
        ? parseFloat(invoice.promo_discount_amount)
        : computedPromoDiscount)
    : 0;

  // Compute subtotal including promo items at real cost
  const effectiveSubtotal = invoice ? (invoice.items || []).reduce((sum, item) => {
    const lt = parseFloat(item.line_total || '0');
    if (lt > 0) return sum + lt;
    if (!item.is_promo) return sum + lt;
    // Promo with $0: add real cost
    const isPkg = item.unit === 'bags' || item.unit === 'sacks' || item.pricing_display_method === 'PER_PACKAGE';
    if (isPkg && item.price_per_package && item.number_of_packages) {
      return sum + parseFloat(item.price_per_package) * item.number_of_packages;
    } else if (item.unit_price_per_lb && item.total_quantity_lbs) {
      return sum + parseFloat(item.unit_price_per_lb) * parseFloat(item.total_quantity_lbs);
    }
    return sum;
  }, 0) : 0;

  // Compute live total for edit mode
  const liveTotal = isEditing && invoice
    ? (effectiveSubtotal
      - effectivePromoDiscount
      - parseFloat(editData.discount_amount || '0')
      + parseFloat(editData.shipping_cost || '0')
      - parseFloat(editData.shipping_discount || '0')
      + parseFloat(editData.tax || '0'))
    : 0;

  // Compute display total (view mode) — used for both Total and Balance Due
  const displayTotal = invoice
    ? (effectiveSubtotal
      - effectivePromoDiscount
      - parseFloat(invoice.discount_amount || '0')
      + parseFloat(invoice.shipping_cost || '0')
      - parseFloat(invoice.shipping_discount || '0')
      + parseFloat(invoice.tax || '0'))
    : 0;

  // Balance Due = displayTotal - payments - BOTH credit types (consistent with displayed Total)
  const computedBalanceDue = invoice
    ? (displayTotal
        - parseFloat(invoice.paid_amount || '0')
        - parseFloat(invoice.credits_applied || '0')
        - parseFloat(invoice.customer_credits_applied || '0'))
    : 0;

  // Shipping override handlers
  const handleSaveShippingOverride = async () => {
    if (!invoice) return;
    setSavingShippingOverride(true);
    setMessage(null);
    try {
      const overrideValue = shippingOverride.trim() === '' ? null : shippingOverride;
      await updateInvoice(invoice.id, {
        shipping_override: overrideValue,
        shipping_override_reason: shippingOverrideReason,
      });
      setInvoice(prev => prev ? { ...prev, shipping_override: overrideValue, shipping_override_reason: shippingOverrideReason } : null);
      setEditingShippingOverride(false);
      setMessage({ type: 'success', text: overrideValue ? 'Shipping override saved!' : 'Shipping override cleared!' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save shipping override' });
    } finally {
      setSavingShippingOverride(false);
    }
  };

  const handleClearShippingOverride = async () => {
    if (!invoice) return;
    setSavingShippingOverride(true);
    setMessage(null);
    try {
      await updateInvoice(invoice.id, { shipping_override: null, shipping_override_reason: '' });
      setInvoice(prev => prev ? { ...prev, shipping_override: null, shipping_override_reason: '' } : null);
      setShippingOverride('');
      setShippingOverrideReason('');
      setEditingShippingOverride(false);
      setMessage({ type: 'success', text: 'Shipping override cleared - using auto-calculated amount' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to clear shipping override' });
    } finally {
      setSavingShippingOverride(false);
    }
  };

  // Quote number handler
  const handleSaveQuoteNumber = async () => {
    if (!invoice) return;
    setSavingQuoteNumber(true);
    setMessage(null);
    try {
      await updateInvoice(invoice.id, { shipping_quote_number: shippingQuoteNumber || '' });
      setInvoice(prev => prev ? { ...prev, shipping_quote_number: shippingQuoteNumber || null } : null);
      setMessage({ type: 'success', text: 'Shipping quote number saved!' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save shipping quote number' });
    } finally {
      setSavingQuoteNumber(false);
    }
  };

  // ── BOL (two-door, one record) ────────────────────────────────────
  // The invoice screen and the mill shipping page share the SAME BOL
  // endpoints: create-or-get via POST /mill/bol/ (returns the existing
  // BOL instead of forking), print via the same PDF generator, and
  // regenerate-from-current-pallets via the shared packaging service.

  const loadBol = async (orderId: number) => {
    try {
      setLoadingBol(true);
      const response = await bolAPI.getForOrder(orderId);
      setBolInfo(response.data.bol || null);
    } catch {
      setBolInfo(null);
    } finally {
      setLoadingBol(false);
    }
  };

  useEffect(() => {
    if (invoice?.order?.id) {
      loadBol(invoice.order.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice?.order?.id]);

  const handleOpenBolCreate = (replaceMode: boolean = false) => {
    if (!invoice?.order) return;
    setBolReplaceMode(replaceMode);
    setBolTrackingNumber('');
    setBolCarrier('FASTWAY');
    setBolShipDate(invoice.order.delivery_date || new Date().toISOString().split('T')[0]);
    setBolQuoteNumber(invoice.shipping_quote_number || '');
    setBolDriverName('');
    setBolTrailerNumber('');
    // Special Instructions starts BLANK — Sibyl decides whether to pull the
    // customer's order notes in (shown in the modal with a "Use these notes"
    // button), edit them, or leave it empty. Not force-pushed.
    setBolSpecialInstructions('');
    setBolPickupNumber('');
    // Liftgate pre-fills from the order; the other services start unchecked.
    setBolLiftgate(!!invoice.order.liftgate_required);
    setBolResidential(false);
    setBolInside(false);
    setBolAppointment(false);
    setBolNotify(false);
    // Re-quote: pre-fill the pallet count with the ACTUAL packed count so
    // Sibyl re-prices against reality; else start from her prior quote.
    if (replaceMode && bolInfo?.pallet_count_discrepancy != null) {
      setBolPalletCount(String(bolInfo.pallet_count_discrepancy));
    } else {
      setBolPalletCount('');
    }
    setBolWeight('');
    setShowBolCreateModal(true);
  };

  const handleCreateBol = async () => {
    if (!invoice?.order || !bolTrackingNumber.trim()) return;
    const palletCount = parseInt(bolPalletCount, 10);
    const weight = parseFloat(bolWeight);
    const hasQuote = !Number.isNaN(palletCount) || !Number.isNaN(weight);
    try {
      setBolWorking(true);
      const response = await bolAPI.create({
        order_ids: [invoice.order.id],
        bol_number: bolTrackingNumber.trim(),
        ship_date: bolShipDate || undefined,
        carrier: bolCarrier,
        quote_number: bolQuoteNumber || undefined,
        driver_name: bolDriverName || undefined,
        trailer_number: bolTrailerNumber || undefined,
        pickup_number: bolPickupNumber || undefined,
        // Always send special_instructions (even '') so the backend uses Sibyl's
        // choice verbatim and never silently re-injects the order's customer notes.
        special_instructions: bolSpecialInstructions,
        // Special services — all print on the BOL; liftgate override sticks.
        liftgate_required: bolLiftgate,
        residential_delivery: bolResidential,
        inside_delivery: bolInside,
        appointment_required: bolAppointment,
        notify_before_delivery: bolNotify,
        // Sibyl's quoted numbers are authoritative — freight is priced per pallet.
        is_quote_sourced: hasQuote || undefined,
        quoted_pallet_count: !Number.isNaN(palletCount) ? palletCount : undefined,
        quoted_weight_lbs: !Number.isNaN(weight) ? weight : undefined,
        replace_existing: bolReplaceMode || undefined,
      });
      if (response.data.existing_bol) {
        // First-writer-wins: the mill already made it — attach that one.
        setMessage({ type: 'success', text: `BOL ${response.data.bol_number} already exists — attached the existing BOL` });
      } else {
        setMessage({ type: 'success', text: `BOL ${response.data.bol_number} ${bolReplaceMode ? 're-quoted (new BOL)' : 'created'}` });
      }
      setShowBolCreateModal(false);
      await loadBol(invoice.order.id);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to create BOL' });
    } finally {
      setBolWorking(false);
    }
  };

  const handlePrintBol = async () => {
    if (!bolInfo) return;
    try {
      setBolWorking(true);
      // Same generator + same record the mill side prints — identical output.
      await bolAPI.generatePDF(bolInfo.id);
      const pdfResponse = await bolAPI.downloadPDF(bolInfo.id);
      const url = window.URL.createObjectURL(new Blob([pdfResponse.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${bolInfo.bol_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      if (invoice?.order) await loadBol(invoice.order.id);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to print BOL' });
    } finally {
      setBolWorking(false);
    }
  };

  const handleRegenerateBol = async () => {
    if (!invoice?.order) return;
    try {
      setBolWorking(true);
      await bolAPI.regenerateForOrder(invoice.order.id);
      setMessage({ type: 'success', text: 'BOL regenerated from the current packed pallets' });
      await loadBol(invoice.order.id);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to regenerate BOL' });
    } finally {
      setBolWorking(false);
    }
  };

  // Expand the BOL's grouped rows into one editable entry PER PHYSICAL PALLET
  // (a row of 2 pallets @ 2,700 → two 2,700 entries), then open the editor.
  const openPalletEditor = () => {
    const rows = bolInfo?.line_items || [];
    const expanded: { weight: string; description: string; nmfc: string; freightClass: string }[] = [];
    rows.forEach(li => {
      for (let i = 0; i < (li.number_of_pallets || 1); i++) {
        expanded.push({
          weight: String(li.weight_lbs), description: li.description || 'Livestock Feed',
          nmfc: li.nmfc_code || '61800', freightClass: li.freight_class || '70',
        });
      }
    });
    if (expanded.length === 0) expanded.push({ weight: '', description: 'Livestock Feed', nmfc: '61800', freightClass: '70' });
    setPalletBuild(expanded);
    setPalletOriginalWeight(Number(bolInfo?.total_weight_lbs || 0));
    setPalletBuildNote(null);
    setShowPalletEditor(true);
  };

  const palletTotal = palletBuild.reduce((s, p) => s + (parseFloat(p.weight) || 0), 0);
  // Warn when the edited total diverges from the packer's on-record weight — the
  // override will NOT update the invoice's shipping charge (billing stays put).
  const palletWeightDiverges = showPalletEditor && palletOriginalWeight > 0
    && Math.round(palletTotal) !== Math.round(palletOriginalWeight);

  const handleSavePalletBuild = async () => {
    if (!bolInfo) return;
    const pallets = palletBuild
      .map(p => ({
        weight_lbs: parseFloat(p.weight), description: p.description.trim() || undefined,
        nmfc_code: p.nmfc.trim() || undefined, freight_class: p.freightClass,
      }))
      .filter(p => !Number.isNaN(p.weight_lbs) && p.weight_lbs > 0);
    if (pallets.length === 0) {
      setMessage({ type: 'error', text: 'Enter at least one pallet with a positive weight.' });
      return;
    }
    try {
      setBolWorking(true);
      const r = await bolAPI.setPalletBuild(bolInfo.id, pallets);
      setPalletBuildNote(r.data.freight_note || null);
      setShowPalletEditor(false);
      setMessage({ type: 'success', text: `Pallet build saved — ${r.data.total_pallets} pallet${r.data.total_pallets !== 1 ? 's' : ''} / ${Number(r.data.total_weight_lbs).toLocaleString()} lbs` });
      if (invoice?.order) await loadBol(invoice.order.id);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save pallet build' });
    } finally {
      setBolWorking(false);
    }
  };

  // BOL details editor: seed from the loaded BOL, PATCH the changed fields.
  const openBolEditor = () => {
    if (!bolInfo) return;
    setBolEdit({
      bol_number: bolInfo.bol_number || '',
      quote_number: bolInfo.quote_number || '',
      ship_date: bolInfo.ship_date || '',
      pro_number: bolInfo.pro_number || '',
      pickup_number: bolInfo.pickup_number || '',
      customer_po_number: bolInfo.customer_po_number || '',
      reference_number: bolInfo.reference_number || '',
      carrier: bolInfo.carrier || 'FASTWAY',
      carrier_name: bolInfo.carrier_name || '',
      driver_name: bolInfo.driver_name || '',
      trailer_number: bolInfo.trailer_number || '',
      seal_number: bolInfo.seal_number || '',
      consignee_name: bolInfo.consignee_name || '',
      consignee_address: bolInfo.consignee_address || '',
      consignee_city: bolInfo.consignee_city || '',
      consignee_state: bolInfo.consignee_state || '',
      consignee_zip: bolInfo.consignee_zip || '',
      consignee_phone: bolInfo.consignee_phone || '',
      special_instructions: bolInfo.special_instructions || '',
      liftgate_required: !!bolInfo.liftgate_required,
      residential_delivery: !!bolInfo.residential_delivery,
      inside_delivery: !!bolInfo.inside_delivery,
      appointment_required: !!bolInfo.appointment_required,
      notify_before_delivery: !!bolInfo.notify_before_delivery,
    });
    setShowBolEditor(true);
  };

  const handleSaveBolDetails = async () => {
    if (!bolInfo) return;
    try {
      setBolWorking(true);
      await bolAPI.update(bolInfo.id, bolEdit as any);
      setShowBolEditor(false);
      setMessage({ type: 'success', text: 'BOL details saved' });
      if (invoice?.order) await loadBol(invoice.order.id);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save BOL details' });
    } finally {
      setBolWorking(false);
    }
  };

  const openReissue = () => {
    if (!bolInfo) return;
    setReissueReason('');
    setReissueNumber(bolInfo.bol_number || '');
    setShowReissue(true);
  };

  const handleReissue = async () => {
    if (!bolInfo || !reissueReason.trim()) return;
    try {
      setBolWorking(true);
      await bolAPI.reissue(bolInfo.id, { reason: reissueReason.trim(), bol_number: reissueNumber.trim() || undefined });
      setShowReissue(false);
      setMessage({ type: 'success', text: `BOL voided and reissued as DRAFT — make your correction below` });
      if (invoice?.order) await loadBol(invoice.order.id);
      openBolEditor();   // open the new DRAFT's edit view so the correction can be made immediately
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to void & reissue BOL' });
    } finally {
      setBolWorking(false);
    }
  };

  // Payment link handler
  const handleSavePaymentLink = async () => {
    if (!invoice) return;
    setSavingPaymentLink(true);
    setMessage(null);
    try {
      await updateInvoice(invoice.id, { payment_link: paymentLink });
      setInvoice(prev => prev ? { ...prev, payment_link: paymentLink || null } : null);
      setEditingPaymentLink(false);
      setMessage({ type: 'success', text: 'Payment link saved!' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save payment link' });
    } finally {
      setSavingPaymentLink(false);
    }
  };

  // Post-approve follow-up state, mirrored from the backend's `follow_up`
  // block (see quickbooks_views.invoice_followup_state).
  //   pending  — approved, Helcim/email/QB still running in Celery
  //   complete — approved, all three finished
  //   partial  — approved, at least one follow-up step did not finish
  //   failed   — approved, but the follow-ups never started (dead broker)
  // NOTE none of these are an approve failure. Only a thrown request is, and
  // the approve endpoint no longer makes any outbound vendor call, so it can
  // no longer be killed mid-flight the way Sentry 31ea0afe recorded.
  const applyFollowUp = (fu: any) => {
    if (!fu) {
      setMessage({ type: 'success', text: 'Invoice approved' });
      return;
    }
    const text = fu.message || 'Invoice approved';
    if (fu.state === 'complete') {
      setHelcimWarning(null);
      setMessage({ type: 'success', text });
    } else if (fu.state === 'pending') {
      setMessage({ type: 'success', text });
    } else {
      // partial / failed — approved, but say which step is outstanding.
      // WARNING, never ERROR: the invoice IS approved and may already be
      // sent, and calling that "Failed to review invoice" is the exact bug
      // that had Sibyl re-approving an invoice the customer already had.
      if (fu.errors?.helcim) {
        setHelcimWarning({ error: fu.errors.helcim });
      }
      setMessage({ type: 'warning', text });
    }
  };

  // The queued follow-ups normally land in a second or two. Poll the invoice
  // a few times so the banner resolves itself instead of leaving the operator
  // staring at "…" — bounded, so a stuck worker cannot poll forever.
  const pollFollowUp = async (invoiceId: number) => {
    const DELAY_MS = 3000;
    const MAX_ATTEMPTS = 10;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, DELAY_MS));
      try {
        const response = await api.get(`/invoices/${invoiceId}/`);
        const fu = response.data?.follow_up;
        if (fu && fu.state !== 'pending') {
          setInvoice(response.data);
          applyFollowUp(fu);
          return;
        }
      } catch {
        // A failed poll says nothing about the approve, which already
        // succeeded. Keep the existing banner and stop quietly.
        return;
      }
    }
  };

  // Review & approve handler — approving NEVER touches shipping (the old
  // shipping box here is what zeroed Osprey Hill's freight).
  const handleReviewApprove = async () => {
    if (!invoice) return;
    const invoiceId = invoice.id;
    setReviewing(true);
    setMessage(null);
    try {
      // invoice_review is now approve-only; Helcim / email / QuickBooks are
      // queued. Response: {success, approved, invoice, follow_up}.
      const result = await reviewInvoice(invoiceId, {
        notes: reviewNotes || undefined,
      }) as any;
      await loadInvoice();
      applyFollowUp(result?.follow_up);
      if (result?.follow_up?.state === 'pending') {
        void pollFollowUp(invoiceId);
      }
    } catch (err: any) {
      // A throw now genuinely means the APPROVE failed — the endpoint does no
      // vendor I/O, so there is no longer a case where the work succeeded and
      // only the response was lost.
      setMessage({
        type: 'error',
        text: err.response?.data?.error || 'Failed to review invoice',
      });
    } finally {
      setReviewing(false);
    }
  };

  // Open send modal and fetch recipients
  const handleOpenSendModal = async () => {
    if (!invoice) return;
    setSendWithTracking(false);
    setShowSendModal(true);
    setLoadingRecipients(true);
    try {
      const data = await getInvoiceRecipients(invoice.id) as any;
      setRecipientOptions(data.portal_users || []);
      setPrimaryEmail(data.primary_email || '');
      const saved = data.saved_recipients || [];
      if (saved.length > 0) {
        setSelectedRecipients(new Set(saved));
      } else {
        setSelectedRecipients(new Set([data.primary_email]));
      }
    } catch {
      // Fallback: just show primary email
      if (invoice.customer) {
        setPrimaryEmail(invoice.customer.email);
        setSelectedRecipients(new Set([invoice.customer.email]));
        setRecipientOptions([]);
      }
    } finally {
      setLoadingRecipients(false);
    }
  };

  // Retry Helcim sync after a previous Save / Save line item / Approve
  // returned helcim_sync='failed'. Hits POST /api/invoices/<id>/sync-helcim/
  // which decides between initial_setup (no ref yet — recovers an
  // approve-failed state) and rebalance (has ref — pushes current totals).
  // Does NOT email the customer; admin clicks Send/Resend separately
  // if needed.
  const handleRetryHelcimSync = async () => {
    if (!invoice) return;
    setRetryingHelcimSync(true);
    setMessage(null);
    try {
      const res = await api.post(`/invoices/${invoice.id}/sync-helcim/`);
      const data = res.data as {
        success: boolean;
        helcim_sync: 'ok' | 'failed' | 'skipped';
        helcim_error?: string;
        recovery_mode?: 'initial_setup' | 'rebalance';
      };
      if (data.helcim_sync === 'ok') {
        // Banner cleared on successful sync — only path that dismisses it
        setHelcimWarning(null);
        await loadInvoice();
        setMessage({ type: 'success', text: 'Helcim sync successful' });
      } else if (data.helcim_sync === 'failed') {
        setHelcimWarning({ error: data.helcim_error || 'unknown error' });
        setMessage({ type: 'error', text: `Retry failed: ${data.helcim_error || 'unknown error'}` });
      } else {
        // 'skipped' — invoice has no Helcim ref AND no setup possible
        // (e.g. no customer). Keep banner visible so admin investigates.
        setMessage({ type: 'warning', text: 'Helcim sync skipped — invoice may need approval first' });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Retry failed — try again or contact support' });
    } finally {
      setRetryingHelcimSync(false);
    }
  };

  // Send invoice to selected recipients. invoice_send is now email-only —
  // no Helcim push from here (that happens on review/edit/sync-helcim).
  // last_emailed_at drives the resent vs sent label so we read it from
  // the pre-send invoice state (after this call it's just been stamped).
  const handleSendToRecipients = async (forceInvoiceOnly: boolean = false) => {
    if (!invoice || selectedRecipients.size === 0) return;
    const isResend = invoice.last_emailed_at !== null;
    const includeTracking = sendWithTracking && !forceInvoiceOnly;
    setSendingInvoice(true);
    setMessage(null);
    setShowSendModal(false);
    try {
      const result = await sendInvoice(invoice.id, Array.from(selectedRecipients), includeTracking) as any;
      loadInvoice();
      loadEmailLogs();
      if (result.email_sent === false) {
        setMessage({ type: 'error', text: 'Invoice send failed — use Resend to retry' });
        return;
      }
      const sentList: string[] = result.recipients_sent || [];
      const recipientStr = sentList.length === 0
        ? 'customer'
        : sentList.length === 1
          ? sentList[0]
          : `${sentList.length} recipients`;
      const verb = isResend ? 'resent' : 'sent';
      const trackingNote = includeTracking ? ' (with tracking)' : '';
      setMessage({ type: 'success', text: `Invoice ${verb} to ${recipientStr}${trackingNote}` });
    } catch (err: any) {
      // WARN behavior for "send with tracking" before the order ships:
      // the backend refuses (409 no_tracking) and Sibyl chooses.
      if (err.response?.status === 409 && err.response?.data?.no_tracking) {
        if (window.confirm('No tracking yet — this order has not shipped.\n\nSend the invoice WITHOUT tracking?')) {
          await handleSendToRecipients(true);
          return;
        }
        setMessage({ type: 'warning', text: 'Send cancelled — no tracking yet. Try again after the order ships.' });
        return;
      }
      setMessage({ type: 'error', text: err.response?.data?.error || err.response?.data?.detail || 'Failed to send invoice' });
    } finally {
      setSendingInvoice(false);
    }
  };

  // Load email logs for sent invoices
  const loadEmailLogs = async () => {
    if (!invoice) return;
    try {
      const data = await getInvoiceEmails(invoice.id) as any;
      setEmailLogs(Array.isArray(data) ? data : []);
    } catch {
      setEmailLogs([]);
    }
  };

  const closeQbWarning = () => setQbSyncWarning(null);

  // Auto-dismiss the QB sync warning after 8 seconds.
  useEffect(() => {
    if (!qbSyncWarning) return;
    const t = setTimeout(() => setQbSyncWarning(null), 8000);
    return () => clearTimeout(t);
  }, [qbSyncWarning]);

  const openVoidModal = (p: Payment) => {
    setVoidingPayment(p);
    setVoidReason('');
    setVoidConfirmChecked(false);
    setVoidError(null);
  };

  const handleVoidPayment = async () => {
    if (!voidingPayment) return;
    setVoidError(null);
    setVoidLoading(true);
    try {
      const res = await api.post<{
        payment: Payment;
        invoice: Invoice;
        qb_synced: boolean;
        qb_warning: string | null;
        deleted_invoice_warning: boolean;
      }>(`/payments/${voidingPayment.id}/void/`, { reason: voidReason });
      setInvoice(res.data.invoice);
      setVoidingPayment(null);
      setVoidReason('');
      setVoidConfirmChecked(false);
      if (!res.data.qb_synced && res.data.qb_warning) {
        setQbSyncWarning(res.data.qb_warning);
      } else {
        setMessage({ type: 'success', text: 'Payment voided.' });
      }
    } catch (err: any) {
      setVoidError(err?.response?.data?.error || 'Failed to void payment');
    } finally {
      setVoidLoading(false);
    }
  };

  const openUnmarkPaidModal = () => {
    setUnmarkReason('');
    setUnmarkConfirmChecked(false);
    setUnmarkError(null);
    setShowUnmarkPaidModal(true);
  };

  const handleUnmarkPaid = async () => {
    if (!invoice) return;
    setUnmarkError(null);
    setUnmarkLoading(true);
    try {
      const res = await api.post<{
        invoice: Invoice;
        voided_payment_count: number;
        total_voided_amount: string;
        qb_synced_count: number;
        qb_failed_count: number;
      }>(`/invoices/${invoice.id}/unmark-paid/`, { reason: unmarkReason });
      setInvoice(res.data.invoice);
      setShowUnmarkPaidModal(false);
      setUnmarkReason('');
      setUnmarkConfirmChecked(false);
      if (res.data.qb_failed_count > 0) {
        setQbSyncWarning(
          `Unmarked ${res.data.voided_payment_count} payment(s) locally. ` +
          `${res.data.qb_failed_count} QB void(s) failed and will be retried.`
        );
      } else {
        setMessage({
          type: 'success',
          text:
            `Unmarked invoice as paid — voided ${res.data.voided_payment_count} ` +
            `payment(s) totaling ${formatCurrency(res.data.total_voided_amount)}.`,
        });
      }
    } catch (err: any) {
      setUnmarkError(err?.response?.data?.error || 'Failed to unmark invoice');
    } finally {
      setUnmarkLoading(false);
    }
  };

  const handleDeleteInvoice = async () => {
    if (!invoice) return;
    setDeleteError(null);
    setDeletingInvoice(true);
    try {
      await api.delete(`/invoices/${invoice.id}/`, {
        data: { reason: deleteReason },
      });
      setShowDeleteModal(false);
      // Bounce back to the invoice list — the row will now live under the Deleted tab.
      navigate('/sales/invoices?tab=deleted');
    } catch (err: any) {
      setDeleteError(err?.response?.data?.error || 'Failed to delete invoice');
    } finally {
      setDeletingInvoice(false);
    }
  };

  const handleRestoreInvoice = async () => {
    if (!invoice) return;
    if (!window.confirm(
      'Restore this invoice? It will become visible to the customer again. ' +
      'No QuickBooks or Helcim re-sync is triggered.'
    )) return;
    setRestoreErrorMsg(null);
    setRestoringInvoice(true);
    try {
      await api.post(`/invoices/${invoice.id}/restore/`);
      loadInvoice();
    } catch (err: any) {
      setRestoreErrorMsg(err?.response?.data?.error || 'Failed to restore invoice');
    } finally {
      setRestoringInvoice(false);
    }
  };

  const handleSyncToQB = async (forceRecreate = false) => {
    if (!invoice) return;
    if (forceRecreate && !window.confirm('Re-create this invoice in QuickBooks? Only use this if you deleted the QB invoice and need to push a fresh copy.')) return;
    setMessage(null);
    try {
      await syncInvoice(invoice.id, forceRecreate);
      loadInvoice();
      const action = forceRecreate ? 'Re-created' : (invoice.qb_invoice_id ? 'Updated' : 'Pushed');
      setMessage({ type: 'success', text: `Invoice ${action} in QuickBooks!` });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to sync to QuickBooks' });
    }
  };

  const handleRecordPayment = async () => {
    if (!invoice || !paymentAmount) return;
    setActionLoading(true);
    setMessage(null);
    try {
      await api.post(`/invoices/${invoice.id}/payments/`, {
        amount: parseFloat(paymentAmount),
        payment_method: paymentMethod,
        reference_number: paymentReference,
      });
      setMessage({ type: 'success', text: 'Payment recorded successfully!' });
      setShowPaymentModal(false);
      setPaymentAmount('');
      setPaymentMethod('check');
      setPaymentReference('');
      loadInvoice();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to record payment' });
    } finally {
      setActionLoading(false);
    }
  };

  const handlePrint = async () => {
    if (!invoice) return;
    setPrintLoading(true);
    try {
      const response = await api.get(`/invoices/${invoice.id}/full/`);
      setFullInvoiceData(response.data);
      setShowPrintView(true);
    } catch (err: any) {
      console.error('Failed to load invoice for printing:', err);
      // Fallback to basic browser print
      window.print();
    } finally {
      setPrintLoading(false);
    }
  };

  const handlePackingSlip = async () => {
    if (!invoice) return;
    setPackingSlipLoading(true);
    try {
      const response = await api.get(`/invoices/${invoice.id}/full/`);
      const d = response.data;
      setPackingSlipData({
        company: d.company,
        customer: d.customer,
        shipping: d.shipping,
        invoice: d.invoice,
        order: d.order,
        line_items: (d.line_items || []).map((item: any) => ({
          product_name: item.product_name,
          description: item.description || '',
          quantity: item.quantity,
          unit: item.unit,
          total_lbs: item.total_lbs,
          bag_size: item.bag_size,
          display_bag_size_lbs: item.display_bag_size_lbs,
          is_promo: item.is_promo,
        })),
      });
      setShowPackingSlip(true);
    } catch (err: any) {
      console.error('Failed to load packing slip data:', err);
      setMessage({ type: 'error', text: 'Failed to load packing slip data' });
    } finally {
      setPackingSlipLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="invoice-detail-page">
        <div className="invoice-detail-loading">Loading invoice...</div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="invoice-detail-page">
        <div className="invoice-detail-error">
          {error || 'Invoice not found'}
          <button onClick={() => (fromCustomer && customerReturnPath) ? navigate(customerReturnPath) : navigate(-1)}>{backLabel}</button>
        </div>
      </div>
    );
  }

  const statusDisplay = getStatusDisplay();
  const hasBalance = computedBalanceDue > 0;

  return (
    <div className={`invoice-detail-page${showPrintView || showPackingSlip ? ' print-overlay-active' : ''}`}>
      {/* Back link — returns to the customer detail page when we arrived from the
          customer Billing tab (explicit path), otherwise browser-back to the invoice
          list so its URL filters + scroll are restored instead of a clean reset. */}
      <Link
        to={backTarget}
        className="back-link"
        onClick={(e) => { if (!(fromCustomer && customerReturnPath)) { e.preventDefault(); navigate(-1); } }}
      >
        &larr; {backLabel}
      </Link>

      {/* Header */}
      <div className="invoice-detail-header">
        <div className="header-left">
          <h1>{invoice.invoice_number || `Invoice #${invoice.id}`}</h1>
          <span
            className="status-badge large"
            style={{ backgroundColor: statusDisplay.bg, color: statusDisplay.text }}
          >
            {statusDisplay.label}
          </span>
          {invoice.qb_invoice_id && !invoice.needs_qb_sync && (
            <span
              className="status-badge"
              style={{ backgroundColor: '#f0fdf4', color: '#166534', fontSize: '0.75rem', marginLeft: '0.5rem' }}
            >
              QB Synced
            </span>
          )}
          {invoice.needs_qb_sync && (
            <span
              className="status-badge"
              style={{ backgroundColor: '#fffbeb', color: '#92400e', fontSize: '0.75rem', marginLeft: '0.5rem' }}
            >
              QB Pending
            </span>
          )}
        </div>
        <div className="header-actions">
          {isEditing ? (
            <div className="edit-actions">
              <button className="btn btn-primary" onClick={handleSaveEdits} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button className="btn btn-secondary" onClick={handleCancelEdit} disabled={saving}>
                Cancel
              </button>
            </div>
          ) : (
            <>
              {canEdit && (
                <button className="btn btn-edit" onClick={handleEnterEdit}>
                  Edit Invoice
                </button>
              )}
              {/* Send/Resend label is dynamic on last_emailed_at, not on
                  status, because invoice_review now sends the first email
                  on approve. Approved invoice with last_emailed_at set →
                  Resend; unset → Send (email failed at approve time). */}
              {(invoice.status === 'approved' || invoice.status === 'sent') && (
                <button
                  className={invoice.last_emailed_at ? 'btn btn-secondary' : 'btn btn-primary'}
                  onClick={handleOpenSendModal}
                  disabled={sendingInvoice}
                  title={
                    invoice.last_emailed_at
                      ? `Last sent ${relativeTime(invoice.last_emailed_at)}`
                      : undefined
                  }
                >
                  {sendingInvoice
                    ? (invoice.last_emailed_at ? 'Resending...' : 'Sending...')
                    : (invoice.last_emailed_at ? 'Resend to Customer' : 'Send to Customer')}
                </button>
              )}
              {hasBalance && (
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowPaymentModal(true)}
                >
                  Record Payment
                </button>
              )}
              <button className="btn btn-secondary" onClick={handlePrint} disabled={printLoading}>
                {printLoading ? 'Loading...' : 'Print / PDF'}
              </button>
              <button className="btn btn-secondary" onClick={handlePackingSlip} disabled={packingSlipLoading}>
                {packingSlipLoading ? 'Loading...' : 'Packing Slip'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => handleSyncToQB(false)}
                style={invoice.needs_qb_sync
                  ? { backgroundColor: '#fffbeb', color: '#92400e', borderColor: '#f59e0b' }
                  : {}
                }
              >
                {!invoice.qb_invoice_id ? 'Push to QB' : invoice.needs_qb_sync ? 'Update in QB' : 'Update in QB'}
              </button>
              {invoice.qb_invoice_id && (
                <button
                  className="btn btn-secondary"
                  onClick={() => handleSyncToQB(true)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', opacity: 0.7 }}
                  title="Delete QB link and re-create from scratch"
                >
                  Re-create in QB
                </button>
              )}
              {invoice.order && (
                <Link to={`/orders/${invoice.order.id}`} className="btn btn-secondary">
                  View Order
                </Link>
              )}
              {/* Bottom-of-stack retry — duplicate of the banner button so
                  admins who scroll past the banner still have the action
                  in the header. Visible only while the warning is active. */}
              {helcimWarning && (
                <button
                  className="btn btn-secondary"
                  onClick={handleRetryHelcimSync}
                  disabled={retryingHelcimSync}
                  style={{ borderColor: '#f59e0b', color: '#92400e' }}
                  title="Re-push current invoice totals to Helcim"
                >
                  {retryingHelcimSync ? 'Retrying...' : 'Retry Helcim Sync'}
                </button>
              )}
              {/* Unmark Paid: bulk-void all active payments. Visible only
                  when the invoice is currently paid and has at least one
                  non-voided payment with a positive amount. */}
              {invoice.status === 'paid' &&
                (invoice.payments || []).some(
                  (p) => !p.voided && parseFloat(p.amount) > 0
                ) && (
                  <button
                    className="btn btn-secondary"
                    onClick={openUnmarkPaidModal}
                    style={{ borderColor: '#92400e', color: '#92400e' }}
                    title="Void all active payments and mark this invoice unpaid"
                  >
                    Unmark Paid
                  </button>
                )}
              {/* Soft-delete action. Hidden once the invoice is already
                  deleted — the deleted-invoice banner above offers Restore. */}
              {!invoice.is_deleted && (
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteModal(true)}
                  style={{ borderColor: '#dc2626', color: '#991b1b' }}
                  title="Soft-delete this invoice (admin only). Numbers are never reused."
                >
                  Delete Invoice
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Deleted-invoice banner. Appears at the top whenever the admin is
          viewing a soft-deleted invoice (Deleted tab → click). Shows who
          deleted, when, and the reason. The Restore button is the only
          way out of this state. */}
      {invoice.is_deleted && (
        <div
          style={{
            backgroundColor: '#fee2e2',
            borderLeft: '4px solid #dc2626',
            color: '#7f1d1d',
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
          }}
        >
          <div style={{ flex: 1 }}>
            <strong>This invoice has been deleted.</strong>
            {invoice.deleted_at && (
              <span> Deleted {formatDate(invoice.deleted_at)}</span>
            )}
            {invoice.deleted_by_name && (
              <span> by {invoice.deleted_by_name}</span>
            )}
            {invoice.deletion_reason && (
              <>
                <span> — </span>
                <em>{invoice.deletion_reason}</em>
              </>
            )}
            {restoreErrorMsg && (
              <div style={{ marginTop: 4, color: '#991b1b' }}>
                {restoreErrorMsg}
              </div>
            )}
          </div>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleRestoreInvoice}
            disabled={restoringInvoice}
            style={{ whiteSpace: 'nowrap' }}
          >
            {restoringInvoice ? 'Restoring…' : 'Restore Invoice'}
          </button>
        </div>
      )}

      {/* Message */}
      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Persistent Helcim-sync-failed banner. Set by Save / Save line item
          / Approve when helcim_sync='failed' is returned. Dismissed only
          by a successful Retry Helcim Sync — never by user click — so a
          drift between local total and Helcim's invoice total can't be
          dismissed-and-forgotten. Sibyl saw this when an undercollected
          invoice's -BAL ref was in terminal state and Save Changes was
          PUTting against the wrong (terminal) Helcim invoice. */}
      {helcimWarning && (
        <div
          style={{
            backgroundColor: '#fef3c7',
            borderLeft: '4px solid #f59e0b',
            color: '#78350f',
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
          }}
        >
          <div style={{ flex: 1 }}>
            <strong>Helcim sync failed:</strong> {helcimWarning.error}. The invoice
            was saved locally but Helcim still has the old version.
          </div>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleRetryHelcimSync}
            disabled={retryingHelcimSync}
            style={{ whiteSpace: 'nowrap' }}
          >
            {retryingHelcimSync ? 'Retrying...' : 'Retry Helcim Sync'}
          </button>
        </div>
      )}

      {/* NO PAYMENT LINK ON AN APPROVED INVOICE (Brad, 2026-11-03: "I need to be able
          to fix and send these invoices"). Approval hands the Helcim link to the
          worker; when that step failed (the worker had no Helcim credentials from
          Sept 2 to Oct 23) the invoice is approved, un-sendable (Send is gated 400
          on payment_link) and the Review & Approve panel is gone. The retry button
          used to exist only inside the in-session failure banner above, so nobody
          could reach it later. Same endpoint, always visible when it is needed. */}
      {invoice && !helcimWarning && !invoice.payment_link
        && !['draft', 'pending_review', 'paid', 'void', 'cancelled'].includes(invoice.status) && (
        <div
          style={{
            backgroundColor: '#fef3c7',
            borderLeft: '4px solid #f59e0b',
            color: '#78350f',
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
          }}
        >
          <div style={{ flex: 1 }}>
            <strong>No payment link.</strong> This invoice was approved but the Helcim
            setup never completed, so it cannot be sent to the customer yet. Creating
            the link sends nothing; use Send to Customer afterwards.
          </div>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleRetryHelcimSync}
            disabled={retryingHelcimSync}
            style={{ whiteSpace: 'nowrap' }}
          >
            {retryingHelcimSync ? 'Creating...' : 'Create Payment Link'}
          </button>
        </div>
      )}

      {/* QB sync warning — surfaced when a void's QB push fails. Auto-dismisses
          after 8s; manual close via the X. retry_qb_sync handles the actual retry. */}
      {qbSyncWarning && (
        <div
          style={{
            backgroundColor: '#fef3c7',
            borderLeft: '4px solid #f59e0b',
            color: '#78350f',
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
          }}
        >
          <div style={{ flex: 1 }}>
            <strong>QuickBooks sync:</strong> {qbSyncWarning}
          </div>
          <button
            onClick={closeQbWarning}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#78350f',
              fontSize: '1.25rem',
              cursor: 'pointer',
              padding: '0 0.5rem',
            }}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      <div className="invoice-detail-content">
        {/* Invoice Info & Customer Info */}
        <div className="invoice-info-grid">
          {/* Invoice Details */}
          <div className="info-card">
            <h3>Invoice Details</h3>
            <div className="info-row">
              <span className="label">Invoice Date</span>
              <span>{formatDate(invoice.created_at)}</span>
            </div>
            <div className="info-row">
              <span className="label">Due Date</span>
              {isEditing ? (
                <input
                  type="date"
                  className="edit-input"
                  value={editData.due_date}
                  onChange={(e) => setEditData({ ...editData, due_date: e.target.value })}
                />
              ) : (
                <span className={invoice.is_overdue ? 'overdue-text' : ''}>
                  {formatDate(invoice.due_date)}
                  {invoice.is_overdue && <span className="overdue-tag"> (Overdue)</span>}
                </span>
              )}
            </div>
            {invoice.customer?.payment_terms && (
              <div className="info-row">
                <span className="label">Payment Terms</span>
                <span>{formatPaymentTerms(invoice.customer.payment_terms)}</span>
              </div>
            )}
            {invoice.order && (
              <div className="info-row">
                <span className="label">Order Number</span>
                <Link to={`/orders/${invoice.order.id}`}>
                  {invoice.order.order_number}
                </Link>
              </div>
            )}
            {invoice.sent_at && (
              <div className="info-row">
                <span className="label">Sent</span>
                <span>{formatDate(invoice.sent_at)}</span>
              </div>
            )}
            {emailLogs.length > 0 && (
              <div className="info-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                <span className="label">Sent to</span>
                {emailLogs.map((log, i) => (
                  <div key={i} style={{ fontSize: 13, color: '#374151' }}>
                    {log.recipient_email}
                    <span style={{ marginLeft: 8, color: log.status === 'delivered' ? '#059669' : log.status === 'failed' ? '#dc2626' : '#6b7280', fontSize: 12 }}>
                      {log.status === 'delivered' ? 'Delivered' : log.status === 'opened' ? 'Opened' : log.status === 'failed' ? 'Failed' : 'Sent'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Customer Info */}
          {invoice.customer && (
            <div className="info-card">
              <h3>Customer</h3>
              <div className="customer-name">{invoice.customer.name}</div>
              {invoice.customer.contact_person && (
                <div className="customer-contact">{invoice.customer.contact_person}</div>
              )}
              {invoice.customer.address && (
                <div className="customer-address">
                  {invoice.customer.address}
                  {invoice.customer.city && (
                    <>, {invoice.customer.city}</>
                  )}
                  {invoice.customer.state && (
                    <>, {invoice.customer.state}</>
                  )}
                  {invoice.customer.postal_code && (
                    <> {invoice.customer.postal_code}</>
                  )}
                </div>
              )}
              {invoice.customer.phone && (
                <div className="customer-phone">{invoice.customer.phone}</div>
              )}
              {invoice.customer.email && (
                <div className="customer-email">
                  <a href={`mailto:${invoice.customer.email}`}>{invoice.customer.email}</a>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Line Items */}
        <div className="line-items-section">
          <h3>Line Items</h3>
          <table className="line-items-table">
            <thead>
              <tr>
                <th>Product</th>
                <th className="text-right">Qty</th>
                <th className="text-right">Unit Price</th>
                <th className="text-right">Total</th>
                {isEditing && <th className="text-center">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {(invoice.items || []).map((item, index) => {
                const isPkgPricing = isPackagePriced(item);
                const isEditingThis = editingItemId === item.id;

                // For promo items: compute real cost if line_total is 0 (pre-Phase-1D data)
                const lineTotal = parseFloat(item.line_total || '0');
                let itemDisplayTotal = item.line_total;
                let promoRealCost = 0;
                if (item.is_promo && lineTotal === 0) {
                  if (isPkgPricing && item.price_per_package && item.number_of_packages) {
                    promoRealCost = parseFloat(item.price_per_package) * item.number_of_packages;
                  } else if (item.unit_price_per_lb && item.total_quantity_lbs) {
                    promoRealCost = parseFloat(item.unit_price_per_lb) * parseFloat(item.total_quantity_lbs);
                  }
                  if (promoRealCost > 0) {
                    itemDisplayTotal = promoRealCost.toFixed(2);
                  }
                }

                // Live-compute total during edit from current edit field values
                const editLiveTotal = isEditingThis
                  ? isPkgPricing
                    ? (parseFloat(editItemData.price_per_package || '0') * parseInt(editItemData.number_of_packages || '0'))
                    : (parseFloat(editItemData.unit_price_per_lb || '0') * parseFloat(editItemData.total_quantity_lbs || '0'))
                  : 0;

                return (
                <tr key={item.id || index} className={isEditingThis ? 'item-editing' : ''}>
                  <td>
                    {item.description_display || item.feed_name}
                    {/* Form lives in description_display now; show the Pellet
                        badge only on the (rare) fallback path. */}
                    {!item.description_display && item.is_pelleted && <span className="pellet-tag">Pellet</span>}
                    {!isEditingThis && item.is_promo && <span className="promo-tag">PROMO</span>}
                    {!isEditingThis && item.is_promo && item.promo_reason && (
                      <span className="promo-reason">{item.promo_reason}</span>
                    )}
                    {!isEditingThis && item.campaign_name && (
                      <span className="campaign-tag">{item.campaign_name}</span>
                    )}
                    {isEditingThis && (
                      <div className="promo-edit-row" style={{ marginTop: 4 }}>
                        <label style={{ fontSize: '0.85em', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <input
                            type="checkbox"
                            checked={editItemData.is_promo}
                            onChange={(e) => setEditItemData({
                              ...editItemData,
                              is_promo: e.target.checked,
                              ...(!e.target.checked ? { promo_reason: '', campaign_enrollment: null } : {}),
                            })}
                          />
                          Promo
                        </label>
                        {editItemData.is_promo && (
                          <>
                            {customerCampaigns.length > 0 && (
                              <select
                                className="edit-input"
                                style={{ fontSize: '0.85em', marginLeft: 8, maxWidth: 180 }}
                                value={editItemData.campaign_enrollment || ''}
                                onChange={(e) => {
                                  const enrollmentId = e.target.value ? parseInt(e.target.value) : null;
                                  const campaign = customerCampaigns.find(c => c.enrollment_id === enrollmentId);
                                  setEditItemData({
                                    ...editItemData,
                                    campaign_enrollment: enrollmentId,
                                    promo_reason: campaign ? campaign.campaign_name : editItemData.promo_reason,
                                  });
                                }}
                              >
                                <option value="">No campaign</option>
                                {customerCampaigns.map(c => (
                                  <option key={c.enrollment_id} value={c.enrollment_id}>{c.campaign_name}</option>
                                ))}
                              </select>
                            )}
                            <input
                              type="text"
                              className="edit-input"
                              style={{ fontSize: '0.85em', marginLeft: 8, maxWidth: 160 }}
                              placeholder="Promo note"
                              value={editItemData.promo_reason}
                              onChange={(e) => setEditItemData({ ...editItemData, promo_reason: e.target.value })}
                            />
                          </>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="text-right">
                    {isEditingThis ? (
                      <div className="inline-edit-group">
                        {isPkgPricing ? (
                          <input
                            type="number"
                            step="1"
                            min="1"
                            className="edit-input edit-input-sm"
                            value={editItemData.number_of_packages}
                            onChange={(e) => setEditItemData({ ...editItemData, number_of_packages: e.target.value })}
                            title={`Number of ${item.unit || 'packages'}`}
                            placeholder={item.unit === 'sacks' ? 'Sacks' : 'Bags'}
                          />
                        ) : (
                          <input
                            type="number"
                            step="0.01"
                            className="edit-input edit-input-sm"
                            value={editItemData.total_quantity_lbs}
                            onChange={(e) => setEditItemData({ ...editItemData, total_quantity_lbs: e.target.value })}
                            title="Total lbs"
                            placeholder="Lbs"
                          />
                        )}
                      </div>
                    ) : (
                      item.quantity_display || `${parseFloat(item.quantity_lbs).toLocaleString()} lbs`
                    )}
                  </td>
                  <td className="text-right">
                    {isEditingThis ? (
                      <div className="inline-edit-group">
                        {isPkgPricing ? (
                          <input
                            type="number"
                            step="0.01"
                            className="edit-input edit-input-sm"
                            value={editItemData.price_per_package}
                            onChange={(e) => setEditItemData({ ...editItemData, price_per_package: e.target.value })}
                            title={`Price per ${item.unit === 'sacks' ? 'sack' : 'bag'}`}
                            placeholder={item.unit === 'sacks' ? '$/sack' : '$/bag'}
                          />
                        ) : (
                          <input
                            type="number"
                            step="0.0001"
                            className="edit-input edit-input-sm"
                            value={editItemData.unit_price_per_lb}
                            onChange={(e) => setEditItemData({ ...editItemData, unit_price_per_lb: e.target.value })}
                            title="Price per lb"
                            placeholder="$/lb"
                          />
                        )}
                      </div>
                    ) : (
                      item.unit_price_display || formatCurrency(item.unit_price)
                    )}
                  </td>
                  <td className="text-right">
                    {isEditingThis ? (
                      <div className="inline-edit-group">
                        <span className="computed-total">{formatCurrency(editLiveTotal || item.line_total)}</span>
                        <button className="btn-icon btn-icon-save" onClick={handleSaveItem} disabled={saving} title="Save">
                          &#10003;
                        </button>
                        <button className="btn-icon btn-icon-cancel" onClick={handleCancelItemEdit} title="Cancel">
                          &#10005;
                        </button>
                      </div>
                    ) : item.is_promo ? (
                      <span className="promo-cost">
                        {formatCurrency(itemDisplayTotal)}
                        <span className="promo-discount-label"> (promo)</span>
                      </span>
                    ) : (
                      formatCurrency(item.line_total)
                    )}
                  </td>
                  {isEditing && (
                    <td className="text-center item-actions">
                      {editingItemId !== item.id && (
                        <>
                          <button
                            className="btn-icon"
                            onClick={() => handleEditItem(item)}
                            title="Edit price"
                            disabled={saving}
                          >
                            &#9998;
                          </button>
                          {(invoice.items || []).length > 1 && (
                            <button
                              className="btn-icon btn-icon-danger"
                              onClick={() => handleDeleteItem(item)}
                              title="Remove item"
                              disabled={saving}
                            >
                              &#10005;
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>

          {/* Add Line Item */}
          {isEditing && invoice && (
            <div className="add-item-section">
              {!showAddItem ? (
                <button
                  className="btn-add-line-item"
                  onClick={() => { setShowAddItem(true); if (formulas.length === 0) loadFormulas(); }}
                >
                  + Add Line Item
                </button>
              ) : (
                <div className="add-item-form">
                  {/* Mode toggle: Feed vs Product */}
                  <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                    <button
                      className={`add-item-mode-btn${newItem.item_mode === 'feed' ? ' active' : ''}`}
                      style={{
                        padding: '4px 12px', border: 'none', borderRadius: '4px', fontSize: '0.8em', fontWeight: 600, cursor: 'pointer',
                        backgroundColor: newItem.item_mode === 'feed' ? '#2D5A27' : '#f0f0f0',
                        color: newItem.item_mode === 'feed' ? '#fff' : '#666',
                      }}
                      onClick={() => setNewItem({ ...newItem, item_mode: 'feed', product_id: null, formula_id: null, price_per_unit: '' })}
                    >
                      Feed
                    </button>
                    <button
                      className={`add-item-mode-btn${newItem.item_mode === 'product' ? ' active' : ''}`}
                      style={{
                        padding: '4px 12px', border: 'none', borderRadius: '4px', fontSize: '0.8em', fontWeight: 600, cursor: 'pointer',
                        backgroundColor: newItem.item_mode === 'product' ? '#2D5A27' : '#f0f0f0',
                        color: newItem.item_mode === 'product' ? '#fff' : '#666',
                      }}
                      onClick={() => setNewItem({ ...newItem, item_mode: 'product', formula_id: null, product_id: null, price_per_unit: '' })}
                    >
                      Other Product
                    </button>
                  </div>

                  <div className="add-item-row">
                    {newItem.item_mode === 'feed' ? (
                      <>
                        <div className="add-item-field add-item-product">
                          <label>Feed Formula</label>
                          <input
                            type="text"
                            className="add-item-search"
                            placeholder="Search formulas..."
                            value={formulaSearch}
                            onChange={(e) => setFormulaSearch(e.target.value)}
                          />
                          <div className="add-item-list">
                            {formulas
                              .filter(f => !formulaSearch || f.name.toLowerCase().includes(formulaSearch.toLowerCase()))
                              .sort((a, b) => a.animal_type.localeCompare(b.animal_type) || a.name.localeCompare(b.name))
                              .map(f => (
                                <div
                                  key={f.id}
                                  className={`add-item-option${newItem.formula_id === f.id ? ' selected' : ''}`}
                                  onClick={() => handleFormulaSelect(f.id)}
                                >
                                  {f.name}
                                </div>
                              ))
                            }
                          </div>
                        </div>
                        <div className="add-item-field">
                          <label>Form</label>
                          <select
                            value={newItem.form}
                            onChange={(e) => handleAddItemFormChange(e.target.value)}
                          >
                            {(getSelectedFormula()?.available_forms || ['MASH', 'PELLET']).map(f => (
                              <option key={f} value={f}>{f}</option>
                            ))}
                          </select>
                        </div>
                        <div className="add-item-field">
                          <label>Bags</label>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={newItem.quantity}
                            onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })}
                          />
                        </div>
                        <div className="add-item-field">
                          <label>Bag Size</label>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={newItem.bag_size}
                            onChange={(e) => setNewItem({ ...newItem, bag_size: parseInt(e.target.value) || 40 })}
                          />
                        </div>
                        <div className="add-item-field">
                          <label>$/Bag</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={newItem.price_per_unit}
                            onChange={(e) => setNewItem({ ...newItem, price_per_unit: e.target.value })}
                            placeholder="0.00"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="add-item-field add-item-product">
                          <label>Product</label>
                          <input
                            type="text"
                            className="add-item-search"
                            placeholder="Search products..."
                            value={productSearch}
                            onChange={(e) => setProductSearch(e.target.value)}
                          />
                          <div className="add-item-list">
                            {catalogProducts
                              .filter(p => !productSearch || p.name.toLowerCase().includes(productSearch.toLowerCase()))
                              .map(p => (
                                <div
                                  key={p.id}
                                  className={`add-item-option${newItem.product_id === p.id ? ' selected' : ''}`}
                                  onClick={() => {
                                    setNewItem({
                                      ...newItem,
                                      product_id: p.id,
                                      price_per_unit: p.default_price || '',
                                      quantity: 1,
                                    });
                                  }}
                                >
                                  <span>{p.name}</span>
                                  <span style={{ fontSize: '0.75em', color: '#888', marginLeft: 4 }}>({p.product_type})</span>
                                </div>
                              ))
                            }
                            {catalogProducts.length === 0 && (
                              <div style={{ padding: '8px', color: '#888', fontSize: '0.85em' }}>
                                No products. Sync from QB in Price Management.
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="add-item-field">
                          <label>Qty</label>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={newItem.quantity}
                            onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })}
                          />
                        </div>
                        <div className="add-item-field">
                          <label>$/Unit</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={newItem.price_per_unit}
                            onChange={(e) => setNewItem({ ...newItem, price_per_unit: e.target.value })}
                            placeholder="0.00"
                          />
                        </div>
                      </>
                    )}
                    <div className="add-item-field">
                      <label>Total</label>
                      <span className="add-item-total">
                        {formatCurrency(newItem.quantity * parseFloat(newItem.price_per_unit || '0'))}
                      </span>
                    </div>
                    <div className="add-item-field add-item-actions">
                      <label>&nbsp;</label>
                      <div>
                        <button
                          className="btn-add-confirm"
                          onClick={handleAddItem}
                          disabled={addingItem || (!newItem.formula_id && !newItem.product_id) || !newItem.price_per_unit}
                        >
                          {addingItem ? 'Adding...' : 'Add'}
                        </button>
                        <button
                          className="btn-add-cancel"
                          onClick={() => {
                            setShowAddItem(false);
                            setNewItem({ formula_id: null, product_id: null, item_mode: 'feed', form: 'MASH', quantity: 1, price_per_unit: '', bag_size: 40, is_promo: false, promo_reason: '', campaign_enrollment: null });
                            setFormulaSearch('');
                            setProductSearch('');
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="add-item-promo-row" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0' }}>
                    <label style={{ fontSize: '0.85em', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <input
                        type="checkbox"
                        checked={newItem.is_promo}
                        onChange={(e) => setNewItem({
                          ...newItem,
                          is_promo: e.target.checked,
                          ...(!e.target.checked ? { promo_reason: '', campaign_enrollment: null } : {}),
                        })}
                      />
                      Promo item
                    </label>
                    {newItem.is_promo && (
                      <>
                        {customerCampaigns.length > 0 && (
                          <select
                            style={{ fontSize: '0.85em', padding: '2px 6px', maxWidth: 200 }}
                            value={newItem.campaign_enrollment || ''}
                            onChange={(e) => {
                              const enrollmentId = e.target.value ? parseInt(e.target.value) : null;
                              const campaign = customerCampaigns.find(c => c.enrollment_id === enrollmentId);
                              setNewItem({
                                ...newItem,
                                campaign_enrollment: enrollmentId,
                                promo_reason: campaign ? campaign.campaign_name : newItem.promo_reason,
                              });
                            }}
                          >
                            <option value="">No campaign</option>
                            {customerCampaigns.map(c => (
                              <option key={c.enrollment_id} value={c.enrollment_id}>{c.campaign_name}</option>
                            ))}
                          </select>
                        )}
                        <input
                          type="text"
                          style={{ fontSize: '0.85em', padding: '2px 6px', maxWidth: 180 }}
                          placeholder="Promo note"
                          value={newItem.promo_reason}
                          onChange={(e) => setNewItem({ ...newItem, promo_reason: e.target.value })}
                        />
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Totals */}
        <div className="totals-section">
          <div className="totals-row">
            <span>Subtotal</span>
            <span>{formatCurrency(effectiveSubtotal)}</span>
          </div>

          {/* Promo Discount - always show if there are promo items */}
          {effectivePromoDiscount > 0 && (
            <div className="totals-row discount-row">
              <span>
                Promo Discount
                {(invoice.items || []).filter(i => i.is_promo).map(i => i.feed_name).length > 0 && (
                  <span className="discount-detail"> ({(invoice.items || []).filter(i => i.is_promo).map(i => i.feed_name).join(', ')})</span>
                )}
              </span>
              <span className="discount-value">-{formatCurrency(effectivePromoDiscount)}</span>
            </div>
          )}

          {/* Shipping Discount - editable */}
          <div className="totals-row discount-row">
            <span>
              Shipping Discount
              {!isEditing && invoice.shipping_discount_reason && (
                <span className="discount-detail"> ({invoice.shipping_discount_reason})</span>
              )}
            </span>
            {isEditing ? (
              <div className="discount-edit-group">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="edit-input edit-input-sm"
                  value={editData.shipping_discount}
                  onChange={(e) => setEditData({ ...editData, shipping_discount: e.target.value })}
                  placeholder="0.00"
                />
                <input
                  type="text"
                  className="edit-input edit-input-reason"
                  value={editData.shipping_discount_reason}
                  onChange={(e) => setEditData({ ...editData, shipping_discount_reason: e.target.value })}
                  placeholder="Reason..."
                />
              </div>
            ) : (
              <span className="discount-value">-{formatCurrency(invoice.shipping_discount)}</span>
            )}
          </div>
          {/* Non-blocking warning (Brad chose warn-and-allow): discounting
              more shipping than is charged is usually meant as a general
              discount — point at Additional Discount but let it save. */}
          {isEditing
            && parseFloat(editData.shipping_discount || '0') > parseFloat(editData.shipping_cost || '0') && (
            <div
              className="totals-row"
              style={{ color: '#b45309', fontSize: '0.85rem', fontStyle: 'italic' }}
            >
              <span>
                ⚠ Shipping discount exceeds the shipping charge — consider
                using Additional Discount instead. Saving is still allowed.
              </span>
            </div>
          )}

          {/* Additional Discount - editable */}
          <div className="totals-row discount-row">
            <span>
              Additional Discount
              {!isEditing && invoice.discount_category_name && (
                <span className="discount-detail"> ({invoice.discount_category_name})</span>
              )}
            </span>
            {isEditing ? (
              <div className="discount-edit-group">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="edit-input edit-input-sm"
                  value={editData.discount_amount}
                  onChange={(e) => setEditData({ ...editData, discount_amount: e.target.value })}
                  placeholder="0.00"
                />
                <select
                  className="edit-input edit-input-reason"
                  value={editData.discount_category || ''}
                  onChange={(e) => {
                    const catId = e.target.value ? Number(e.target.value) : null;
                    const cat = discountCategories.find(c => c.id === catId);
                    setEditData({ ...editData, discount_category: catId, discount_reason: cat?.name || '' });
                  }}
                >
                  <option value="">-- Select Category --</option>
                  {discountCategories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
                {discountCategories.find(c => c.id === editData.discount_category)?.is_custom && (
                  <textarea
                    className="edit-input"
                    style={{ width: '100%', marginTop: 4, fontSize: '0.85rem' }}
                    value={editData.discount_notes}
                    onChange={(e) => setEditData({ ...editData, discount_notes: e.target.value })}
                    placeholder="Describe discount reason..."
                    rows={2}
                  />
                )}
              </div>
            ) : (
              <span className="discount-value">-{formatCurrency(invoice.discount_amount)}</span>
            )}
          </div>
          {!isEditing && invoice.discount_notes && (
            <div className="totals-row" style={{ fontSize: '0.8rem', color: '#666' }}>
              <span>Discount Notes</span>
              <span>{invoice.discount_notes}</span>
            </div>
          )}

          <div className="totals-row">
            <span>Shipping</span>
            {isEditing ? (
              <input
                type="number"
                step="0.01"
                className="edit-input edit-input-sm"
                value={editData.shipping_cost}
                onChange={(e) => setEditData({ ...editData, shipping_cost: e.target.value })}
              />
            ) : (
              <span>{formatCurrency(invoice.shipping_cost)}</span>
            )}
          </div>
          <div className="totals-row">
            <span>Tax</span>
            {isEditing ? (
              <input
                type="number"
                step="0.01"
                className="edit-input edit-input-sm"
                value={editData.tax}
                onChange={(e) => setEditData({ ...editData, tax: e.target.value })}
              />
            ) : (
              <span>{formatCurrency(invoice.tax)}</span>
            )}
          </div>
          <div className="totals-row total">
            <span>Total</span>
            <span className={isEditing ? 'live-total' : ''}>
              {isEditing
                ? formatCurrency(liveTotal)
                : formatCurrency(displayTotal)
              }
            </span>
          </div>
          {parseFloat(invoice.credits_applied || '0') > 0 && (
            <div className="totals-row credits">
              <span>Bridge Club Credit</span>
              <span>-{formatCurrency(invoice.credits_applied)}</span>
            </div>
          )}
          {parseFloat(invoice.customer_credits_applied || '0') > 0 && (
            <div className="totals-row credits">
              <span>Account Credit</span>
              <span>-{formatCurrency(invoice.customer_credits_applied || '0')}</span>
            </div>
          )}
          {parseFloat(invoice.paid_amount) > 0 && (
            <div className="totals-row paid">
              <span>Amount Paid</span>
              <span>-{formatCurrency(invoice.paid_amount)}</span>
            </div>
          )}
          <div className={`totals-row balance ${hasBalance ? 'due' : 'paid'}`}>
            <span>Balance Due</span>
            <span>{formatCurrency(computedBalanceDue)}</span>
          </div>
          {invoice.credit_applications && invoice.credit_applications.length > 0 && (
            <div className="totals-row" style={{ display: 'block', marginTop: 8, fontSize: '0.8rem', color: '#6b7280' }}>
              {invoice.credit_applications.map((app, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>
                    {app.credit_type_display}
                    {app.reference ? ` (${app.reference})` : ''}
                    {app.applied_at ? ` · ${new Date(app.applied_at).toLocaleDateString()}` : ''}
                  </span>
                  <span>-{formatCurrency(app.amount_applied)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Shipping Details Section — single home for freight info: the
            quote number (prints on the BOL) up top, override below. */}
        {canEdit && (
          <div className="info-card shipping-override-section">
            <h3>Shipping Details</h3>

            {/* Freight Quote # — ties through to the BOL: new BOLs pre-fill
                from this, and saving a change here propagates to this
                order's un-shipped BOLs. */}
            <div className="quote-number-row" style={{ marginBottom: '0.75rem' }}>
              <div className="form-group-inline">
                <label>Freight Quote # (prints on BOL)</label>
                <input
                  type="text"
                  className="edit-input edit-input-sm"
                  value={shippingQuoteNumber}
                  onChange={(e) => setShippingQuoteNumber(e.target.value)}
                  placeholder="Carrier quote number..."
                />
                <button className="btn btn-secondary btn-sm" onClick={handleSaveQuoteNumber} disabled={savingQuoteNumber}>
                  {savingQuoteNumber ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>

            {editingShippingOverride ? (
              <div className="override-edit">
                <div className="form-group">
                  <label>Override Amount ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="edit-input"
                    value={shippingOverride}
                    onChange={(e) => setShippingOverride(e.target.value)}
                    placeholder="Leave blank to use auto-calculated"
                  />
                </div>
                <div className="form-group">
                  <label>Reason</label>
                  <input
                    type="text"
                    className="edit-input"
                    value={shippingOverrideReason}
                    onChange={(e) => setShippingOverrideReason(e.target.value)}
                    placeholder="Why override shipping cost?"
                  />
                </div>
                <div className="override-actions">
                  <button className="btn btn-primary btn-sm" onClick={handleSaveShippingOverride} disabled={savingShippingOverride}>
                    {savingShippingOverride ? 'Saving...' : 'Save Override'}
                  </button>
                  {invoice.shipping_override && (
                    <button className="btn btn-danger btn-sm" onClick={handleClearShippingOverride} disabled={savingShippingOverride}>
                      Clear Override
                    </button>
                  )}
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditingShippingOverride(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="override-display">
                {invoice.shipping_override ? (
                  <div className="info-row">
                    <span className="label">Override</span>
                    <span>
                      {formatCurrency(invoice.shipping_override)}
                      {invoice.shipping_override_reason && (
                        <span className="override-reason"> ({invoice.shipping_override_reason})</span>
                      )}
                    </span>
                  </div>
                ) : (
                  <div className="info-row">
                    <span className="label">Shipping</span>
                    <span>{formatCurrency(invoice.shipping_cost)} (auto-calculated)</span>
                  </div>
                )}
                <button className="btn btn-secondary btn-sm" onClick={() => setEditingShippingOverride(true)}>
                  {invoice.shipping_override ? 'Edit Override' : 'Set Override'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Bill of Lading — the SAME record the mill shipping page
            makes/prints (one BOL per shipment, built from the packed
            pallets). Create here if Sibyl gets to it first; the mill
            pulls it at shipment. Regenerate rebuilds it from the
            current pallets after a re-pack. */}
        {invoice.order && (
          <div className="info-card bol-section">
            <h3>Bill of Lading</h3>
            {loadingBol ? (
              <p style={{ color: '#6b7280', margin: 0 }}>Loading BOL...</p>
            ) : bolInfo ? (
              <div>
                {/* Re-quote banner: quote-sourced BOL whose quoted pallet count
                    differs from the actual packed count. Freight is priced per
                    pallet, so this is a re-quote event — Sibyl re-quotes + makes
                    a NEW BOL; never a silent override. */}
                {bolInfo.pallet_count_discrepancy != null && (
                  <div style={{ marginBottom: 12, padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
                    <div style={{ fontWeight: 700, color: '#991b1b' }}>⚠ Re-quote needed</div>
                    <div style={{ fontSize: 13, color: '#7f1d1d', marginTop: 2 }}>
                      BOL quoted {bolInfo.quoted_pallet_count} pallet{bolInfo.quoted_pallet_count !== 1 ? 's' : ''}, packed as {bolInfo.pallet_count_discrepancy}. Re-quote freight and generate a corrected BOL.
                    </div>
                    {canEdit && (
                      <button className="bol-btn bol-btn-danger bol-btn-sm" style={{ marginTop: 8 }} onClick={() => handleOpenBolCreate(true)} disabled={bolWorking}>
                        Re-quote & Replace BOL
                      </button>
                    )}
                  </div>
                )}
                <div className="info-row">
                  <span className="label">BOL #</span>
                  <span style={{ fontWeight: 600 }}>
                    {bolInfo.bol_number}
                    <span style={{ fontWeight: 400, color: '#6b7280' }}> · {bolInfo.status_display || bolInfo.status}</span>
                    {bolInfo.is_quote_sourced && (
                      <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 10, background: '#dbeafe', color: '#1e40af', fontSize: '0.75em', fontWeight: 700 }}>
                        QUOTED
                      </span>
                    )}
                    {bolInfo.is_stale && (
                      <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 10, background: '#fef3c7', color: '#92400e', fontSize: '0.75em', fontWeight: 700 }}>
                        STALE — pallets changed
                      </span>
                    )}
                    {bolInfo.reissued_from && (
                      <span title={`Reissued from voided BOL ${bolInfo.reissued_from_bol_number || ''}`}
                        style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 10, background: '#ede9fe', color: '#5b21b6', fontSize: '0.75em', fontWeight: 700 }}>
                        REISSUED{bolInfo.reissued_from_bol_number ? ` — from ${bolInfo.reissued_from_bol_number}` : ''}
                      </span>
                    )}
                  </span>
                </div>
                <div className="info-row">
                  <span className="label">Freight</span>
                  <span>
                    {bolInfo.carrier_display} · ships {bolInfo.ship_date} · {(bolInfo.display_pallet_count ?? bolInfo.total_pallets) || 0} pallet{((bolInfo.display_pallet_count ?? bolInfo.total_pallets) || 0) !== 1 ? 's' : ''} / {((bolInfo.display_weight_lbs ?? bolInfo.total_weight_lbs) || 0).toLocaleString()} lbs
                    {bolInfo.is_quote_sourced && <span style={{ color: '#6b7280', fontSize: 12 }}> (quoted)</span>}
                  </span>
                </div>
                {bolInfo.created_by && (
                  <div className="info-row">
                    <span className="label">Created by</span>
                    <span>
                      {bolInfo.created_by}
                      {bolInfo.created_at ? ` · ${new Date(bolInfo.created_at).toLocaleString()}` : ''}
                    </span>
                  </div>
                )}
                {bolInfo.pallets_manually_edited && (
                  <div className="info-row">
                    <span className="label">Pallet build</span>
                    <span style={{ padding: '2px 8px', borderRadius: 10, background: '#dcfce7', color: '#166534', fontSize: '0.75em', fontWeight: 700 }}>
                      MANUAL OVERRIDE — won't rebuild on regenerate
                    </span>
                  </div>
                )}
                {palletBuildNote && (
                  <div style={{ marginTop: 6, padding: 8, background: '#fef3c7', color: '#92400e', borderRadius: 6, fontSize: 13 }}>
                    {palletBuildNote}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: '0.5rem', flexWrap: 'wrap' }}>
                  <button className="bol-btn bol-btn-primary bol-btn-sm" onClick={handlePrintBol} disabled={bolWorking}>
                    {bolWorking ? 'Working...' : 'Print BOL'}
                  </button>
                  {canEdit && (
                    <button className="bol-btn bol-btn-secondary bol-btn-sm" onClick={openBolEditor} disabled={bolWorking}
                      title="Edit every field that prints on the BOL">
                      Edit BOL Details
                    </button>
                  )}
                  {canEdit && (
                    <button className="bol-btn bol-btn-secondary bol-btn-sm" onClick={openPalletEditor} disabled={bolWorking}
                      title="Combine/split pallets and set the count that prints on the BOL">
                      Edit Pallet Build
                    </button>
                  )}
                  <button className="bol-btn bol-btn-secondary bol-btn-sm" onClick={handleRegenerateBol} disabled={bolWorking}
                    title={bolInfo.pallets_manually_edited
                      ? 'Discard the manual override and rebuild from the current packed pallets'
                      : 'Rebuild this BOL from the current packed pallets (after a re-pack)'}>
                    Regenerate from Pallets
                  </button>
                  {/* Post-shipment correction — SHIPPED only; DELIVERED stays locked. */}
                  {canEdit && bolInfo.status === 'SHIPPED' && (
                    <button className="bol-btn bol-btn-danger bol-btn-sm" onClick={openReissue} disabled={bolWorking}
                      title="Void this shipped BOL and reissue a corrected one reusing the same number">
                      Void &amp; Reissue
                    </button>
                  )}
                </div>

                {showReissue && (
                  <div style={{ marginTop: 12, padding: 12, border: '1px solid #fecaca', borderRadius: 8, background: '#fef2f2' }}>
                    <div style={{ fontWeight: 700, color: '#991b1b', marginBottom: 4 }}>Void &amp; Reissue BOL</div>
                    <p style={{ margin: '0 0 8px', color: '#7f1d1d', fontSize: 13 }}>
                      The current BOL <strong>{bolInfo.bol_number}</strong> will be <strong>voided</strong> and a corrected
                      copy created as a new DRAFT — destination, routing, and pallet build carry forward; PRO/driver/trailer/seal
                      are cleared for re-entry. The reissue reuses the carrier's sticker number by default.
                    </p>
                    <div className="form-group" style={{ marginBottom: 8 }}>
                      <label style={{ fontSize: 12 }}>Reason (required)</label>
                      <input type="text" className="edit-input" value={reissueReason}
                        onChange={e => setReissueReason(e.target.value)} placeholder="Why is this BOL being corrected?" />
                    </div>
                    <div className="form-group" style={{ marginBottom: 8 }}>
                      <label style={{ fontSize: 12 }}>BOL # for the reissue</label>
                      <input type="text" className="edit-input" value={reissueNumber}
                        onChange={e => setReissueNumber(e.target.value)} />
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button className="bol-btn bol-btn-danger bol-btn-sm" onClick={handleReissue}
                        disabled={bolWorking || !reissueReason.trim()}>
                        {bolWorking ? 'Working...' : 'Void & Reissue'}
                      </button>
                      <button className="bol-btn bol-btn-secondary bol-btn-sm" onClick={() => setShowReissue(false)} disabled={bolWorking}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {showPalletEditor && (
                  <div style={{ marginTop: 12, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>Pallet build</div>
                    <p style={{ margin: '0 0 8px', color: '#6b7280', fontSize: 12 }}>
                      One row per pallet you're actually shipping. Combine products onto a pallet by
                      deleting a row and adding its weight to another; split by adding a row. This
                      prints on the BOL and won't be rebuilt by the packer.
                    </p>
                    {palletBuild.map((p, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ width: 60, color: '#6b7280', fontSize: 13 }}>Pallet {i + 1}</span>
                        <input type="number" min="0" value={p.weight} placeholder="lbs (gross)"
                          onChange={e => setPalletBuild(b => b.map((x, j) => j === i ? { ...x, weight: e.target.value } : x))}
                          style={{ width: 100, padding: 6 }} />
                        <input type="text" value={p.description} placeholder="Description"
                          onChange={e => setPalletBuild(b => b.map((x, j) => j === i ? { ...x, description: e.target.value } : x))}
                          style={{ flex: 1, minWidth: 120, padding: 6 }} />
                        <input type="text" value={p.nmfc} placeholder="NMFC" title="NMFC code"
                          onChange={e => setPalletBuild(b => b.map((x, j) => j === i ? { ...x, nmfc: e.target.value } : x))}
                          style={{ width: 70, padding: 6 }} />
                        <select value={p.freightClass} title="Freight class"
                          onChange={e => setPalletBuild(b => b.map((x, j) => j === i ? { ...x, freightClass: e.target.value } : x))}
                          style={{ width: 78, padding: 6 }}>
                          {FREIGHT_CLASSES.map(fc => <option key={fc} value={fc}>{fc}</option>)}
                        </select>
                        <button className="bol-btn bol-btn-secondary bol-btn-sm" title="Remove pallet"
                          onClick={() => setPalletBuild(b => b.filter((_, j) => j !== i))}>✕</button>
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
                      <button className="bol-btn bol-btn-secondary bol-btn-sm"
                        onClick={() => setPalletBuild(b => [...b, { weight: '', description: 'Livestock Feed', nmfc: '61800', freightClass: '70' }])}>
                        + Add pallet
                      </button>
                      <span style={{ color: '#374151', fontSize: 13, fontWeight: 600 }}>
                        {palletBuild.length} pallet{palletBuild.length !== 1 ? 's' : ''} · {palletTotal.toLocaleString()} lbs
                      </span>
                      {bolInfo.quoted_pallet_count != null && palletBuild.length !== bolInfo.quoted_pallet_count && (
                        <span style={{ color: '#92400e', fontSize: 12 }}>
                          (quoted {bolInfo.quoted_pallet_count} — freight may differ)
                        </span>
                      )}
                    </div>
                    {palletWeightDiverges && (
                      <div style={{ marginTop: 8, padding: 8, background: '#fef3c7', color: '#92400e', borderRadius: 6, fontSize: 12 }}>
                        This changes the BOL weight ({palletTotal.toLocaleString()} lbs vs {palletOriginalWeight.toLocaleString()} lbs on record).
                        The BOL will print your weight, but this does <strong>not</strong> update the invoice's shipping charge.
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <button className="bol-btn bol-btn-primary bol-btn-sm" onClick={handleSavePalletBuild} disabled={bolWorking}>
                        {bolWorking ? 'Saving...' : 'Save Pallet Build'}
                      </button>
                      <button className="bol-btn bol-btn-secondary bol-btn-sm" onClick={() => setShowPalletEditor(false)} disabled={bolWorking}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {showBolEditor && (() => {
                  const set = (k: string, v: string | boolean) => setBolEdit(e => ({ ...e, [k]: v }));
                  const txt = (k: string, label: string, edited?: boolean, ph?: string) => (
                    <div className="form-group" style={{ marginBottom: 8 }}>
                      <label style={{ fontSize: 12 }}>{label}{edited && <EditedChip />}</label>
                      <input type="text" className="edit-input" value={String(bolEdit[k] ?? '')}
                        placeholder={ph} onChange={e => set(k, e.target.value)} />
                    </div>
                  );
                  return (
                  <div style={{ marginTop: 12, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                    <div style={{ fontWeight: 700, marginBottom: 8 }}>Edit BOL details</div>

                    <div style={{ fontWeight: 600, fontSize: 13, margin: '4px 0' }}>Header / Reference</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      {txt('bol_number', 'Tracking/BOL #')}
                      {txt('quote_number', 'Quote #', bolInfo.quote_number_manually_edited)}
                      <div className="form-group" style={{ marginBottom: 8 }}>
                        <label style={{ fontSize: 12 }}>Ship Date{bolInfo.ship_date_manually_edited && <EditedChip />}</label>
                        <input type="date" className="edit-input" value={String(bolEdit.ship_date ?? '')}
                          onChange={e => set('ship_date', e.target.value)} />
                      </div>
                      {txt('pickup_number', 'Pickup #')}
                      {txt('pro_number', 'PRO #')}
                      {txt('customer_po_number', 'Customer PO #', bolInfo.customer_po_number_manually_edited)}
                      {txt('reference_number', 'Reference #')}
                    </div>

                    <div style={{ fontWeight: 600, fontSize: 13, margin: '10px 0 4px' }}>
                      Consignee{bolInfo.consignee_manually_edited && <EditedChip />}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      {txt('consignee_name', 'Name')}
                      {txt('consignee_phone', 'Phone')}
                      {txt('consignee_address', 'Address')}
                      {txt('consignee_city', 'City')}
                      {txt('consignee_state', 'State')}
                      {txt('consignee_zip', 'ZIP')}
                    </div>

                    <div style={{ fontWeight: 600, fontSize: 13, margin: '10px 0 4px' }}>Carrier &amp; Routing</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <div className="form-group" style={{ marginBottom: 8 }}>
                        <label style={{ fontSize: 12 }}>Carrier</label>
                        <select className="edit-input" value={String(bolEdit.carrier ?? '')} onChange={e => set('carrier', e.target.value)}>
                          <option value="FASTWAY">Fastway Freight</option>
                          <option value="PENINSULA">Peninsula Truck Lines</option>
                          <option value="OAK_HARBOR">Oak Harbor Freight</option>
                          <option value="DIAMOND_LINE">Diamond Line Delivery</option>
                          <option value="UPS_FREIGHT">UPS Freight</option>
                          <option value="OTHER">Other</option>
                        </select>
                      </div>
                      {bolEdit.carrier === 'OTHER' && txt('carrier_name', 'Carrier name (Other)')}
                      {txt('driver_name', 'Driver')}
                      {txt('trailer_number', 'Trailer #')}
                      {txt('seal_number', 'Seal #')}
                    </div>

                    <div style={{ fontWeight: 600, fontSize: 13, margin: '10px 0 4px' }}>
                      Special Services{bolInfo.liftgate_manually_edited && <EditedChip />}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 1.25rem' }}>
                      {([
                        ['liftgate_required', 'Liftgate Required'],
                        ['residential_delivery', 'Residential Delivery'],
                        ['inside_delivery', 'Inside Delivery'],
                        ['appointment_required', 'Appointment Required'],
                        ['notify_before_delivery', 'Call Before Delivery'],
                      ] as [string, string][]).map(([k, label]) => (
                        <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 400, cursor: 'pointer', fontSize: 13 }}>
                          <input type="checkbox" checked={!!bolEdit[k]} onChange={e => set(k, e.target.checked)} />
                          {label}
                        </label>
                      ))}
                    </div>

                    <div style={{ fontWeight: 600, fontSize: 13, margin: '10px 0 4px' }}>Instructions</div>
                    <textarea className="edit-input" rows={2} value={String(bolEdit.special_instructions ?? '')}
                      onChange={e => set('special_instructions', e.target.value)} placeholder="Prints on the BOL" />

                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      <button className="bol-btn bol-btn-primary bol-btn-sm" onClick={handleSaveBolDetails} disabled={bolWorking}>
                        {bolWorking ? 'Saving...' : 'Save BOL Details'}
                      </button>
                      <button className="bol-btn bol-btn-secondary bol-btn-sm" onClick={() => setShowBolEditor(false)} disabled={bolWorking}>
                        Cancel
                      </button>
                    </div>
                  </div>
                  );
                })()}
              </div>
            ) : (
              <div>
                <p style={{ color: '#6b7280', marginTop: 0 }}>
                  No BOL yet for order {invoice.order.order_number}. Create it here and the mill
                  crew will pull this same BOL at shipment — or leave it and they'll create it then.
                </p>
                {canEdit && (
                  <button className="bol-btn bol-btn-primary bol-btn-sm" onClick={() => handleOpenBolCreate(false)}>
                    Create & Attach BOL
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Payment Link Section */}
        {canEdit && (
          <div className="info-card payment-link-section">
            <h3>Payment Link</h3>
            {editingPaymentLink ? (
              <div className="override-edit">
                <div className="form-group">
                  <label>Payment URL</label>
                  <input
                    type="url"
                    className="edit-input"
                    value={paymentLink}
                    onChange={(e) => setPaymentLink(e.target.value)}
                    placeholder="https://..."
                  />
                </div>
                <div className="override-actions">
                  <button className="btn btn-primary btn-sm" onClick={handleSavePaymentLink} disabled={savingPaymentLink}>
                    {savingPaymentLink ? 'Saving...' : 'Save Link'}
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setEditingPaymentLink(false); setPaymentLink(invoice.payment_link || ''); }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="override-display">
                {invoice.payment_link ? (
                  <div className="info-row">
                    <span className="label">Link</span>
                    <a href={invoice.payment_link} target="_blank" rel="noopener noreferrer" className="payment-url">
                      {invoice.payment_link}
                    </a>
                  </div>
                ) : (
                  <div className="info-row">
                    <span className="label">No payment link set</span>
                  </div>
                )}
                {/* Sync + email status indicators. helcim_balance_synced_at
                    is stamped after every successful Helcim push (initial
                    setup, post-edit rebalance, manual sync). last_emailed_at
                    is stamped after every successful customer send. */}
                {invoice.helcim_balance_synced_at && (
                  <div className="info-row" style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                    <span className="label">Helcim synced</span>
                    <span>{relativeTime(invoice.helcim_balance_synced_at)}</span>
                  </div>
                )}
                {invoice.status === 'approved' && !invoice.last_emailed_at && (
                  <div className="info-row" style={{ fontSize: '0.85rem' }}>
                    <span className="label">Customer email</span>
                    <span style={{ color: '#dc2626' }}>not yet emailed</span>
                  </div>
                )}
                {invoice.last_emailed_at && (
                  <div className="info-row" style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                    <span className="label">Customer emailed</span>
                    <span>{relativeTime(invoice.last_emailed_at)}</span>
                  </div>
                )}
                <button className="btn btn-secondary btn-sm" onClick={() => setEditingPaymentLink(true)}>
                  {invoice.payment_link ? 'Edit Link' : 'Add Payment Link'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Review & Approve Section — shipping is read-only here; the old
            editable box defaulted blank→$0 and silently zeroed real
            shipping on approve. Edit shipping via Edit mode instead. */}
        {invoice.status === 'pending_review' && (
          <div className="info-card review-section">
            <h3>Review & Approve</h3>
            <div className="info-row">
              <span className="label">Shipping (edit via Edit mode)</span>
              <span>{formatCurrency(invoice.shipping_cost)}</span>
            </div>
            <div className="form-group">
              <label>Review Notes</label>
              <textarea
                className="edit-textarea"
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                rows={2}
                placeholder="Optional notes..."
              />
            </div>
            <button className="btn btn-approve" onClick={handleReviewApprove} disabled={reviewing}>
              {reviewing ? 'Approving...' : 'Approve & Email Customer'}
            </button>
          </div>
        )}

        {/* Payment History */}
        {(invoice.payments || []).length > 0 && (
          <div className="payments-section">
            <h3>Payment History</h3>
            <table className="payments-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Method</th>
                  <th>Reference</th>
                  <th>Taken By</th>
                  <th className="text-right">Amount</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(invoice.payments || []).map((payment) => (
                  <React.Fragment key={payment.id}>
                    <tr style={payment.voided ? { textDecoration: 'line-through', color: '#9ca3af' } : undefined}>
                      <td>{formatDate(payment.payment_date)}</td>
                      <td>{payment.payment_method}</td>
                      <td>{payment.reference_number || '—'}</td>
                      <td>{payment.recorded_by_name || '—'}</td>
                      <td className="text-right">{formatCurrency(payment.amount)}</td>
                      <td>
                        {payment.voided ? (
                          <span
                            className="status-badge"
                            style={{ backgroundColor: '#fee2e2', color: '#991b1b' }}
                          >
                            VOIDED
                          </span>
                        ) : (
                          <button
                            className="btn btn-secondary"
                            style={{ borderColor: '#dc2626', color: '#991b1b', fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                            onClick={() => openVoidModal(payment)}
                          >
                            Void Payment
                          </button>
                        )}
                      </td>
                    </tr>
                    {payment.voided && (
                      <tr style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                        <td colSpan={6} style={{ paddingLeft: '2rem' }}>
                          Voided {payment.voided_at ? formatDate(payment.voided_at) : ''}
                          {payment.voided_by_name && ` by ${payment.voided_by_name}`}
                          {payment.void_reason && ` — ${payment.void_reason}`}
                          {payment.qb_payment_synced && !payment.qb_void_synced && (
                            <span style={{ marginLeft: 8, color: '#92400e' }}>
                              (QB void retry pending)
                            </span>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Notes / Special Instructions */}
        {isEditing ? (
          <div className="notes-section">
            <div className="note-block">
              <h4>Customer Notes</h4>
              <textarea
                className="edit-textarea"
                value={editData.customer_notes}
                onChange={(e) => setEditData({ ...editData, customer_notes: e.target.value })}
                rows={3}
                placeholder="Notes visible to customer..."
              />
            </div>
            <div className="note-block">
              <h4>Internal Notes</h4>
              <textarea
                className="edit-textarea"
                value={editData.notes}
                onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                rows={3}
                placeholder="Internal notes..."
              />
            </div>
          </div>
        ) : (
          (invoice.order?.special_instructions || invoice.notes || invoice.customer_notes) && (
            <div className="notes-section">
              {invoice.order?.special_instructions && (
                <div className="note-block">
                  <h4>Special Instructions</h4>
                  <p>{invoice.order.special_instructions}</p>
                </div>
              )}
              {invoice.customer_notes && (
                <div className="note-block">
                  <h4>Customer Notes</h4>
                  <p>{invoice.customer_notes}</p>
                </div>
              )}
              {invoice.notes && (
                <div className="note-block">
                  <h4>Internal Notes</h4>
                  <p>{invoice.notes}</p>
                </div>
              )}
            </div>
          )
        )}
      </div>

      {/* Send Invoice Modal */}
      {showSendModal && (
        <div className="modal-overlay" onClick={() => setShowSendModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <h3 style={{ marginBottom: 16 }}>
              {invoice?.status === 'sent' ? 'Resend' : 'Send'} Invoice {invoice?.invoice_number}
            </h3>
            {loadingRecipients ? (
              <p>Loading recipients...</p>
            ) : recipientOptions.length === 0 ? (
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
                  <input type="checkbox" checked disabled />
                  <span>{primaryEmail}</span>
                  <span style={{ color: '#6b7280', fontSize: 13 }}>(Primary)</span>
                </label>
              </div>
            ) : (
              <div>
                {recipientOptions.map(u => {
                  const isPrimary = u.email === primaryEmail;
                  return (
                    <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={selectedRecipients.has(u.email)}
                        onChange={() => {
                          const next = new Set(selectedRecipients);
                          if (next.has(u.email)) next.delete(u.email);
                          else next.add(u.email);
                          setSelectedRecipients(next);
                        }}
                      />
                      <span>{u.email}</span>
                      <span style={{ color: '#6b7280', fontSize: 13 }}>
                        ({u.name} — {u.role.charAt(0).toUpperCase() + u.role.slice(1)})
                      </span>
                      {isPrimary && <span style={{ color: '#2563eb', fontSize: 12, fontWeight: 600 }}>Primary</span>}
                    </label>
                  );
                })}
                {/* Include primary email if not a portal user */}
                {primaryEmail && !recipientOptions.some(u => u.email === primaryEmail) && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selectedRecipients.has(primaryEmail)}
                      onChange={() => {
                        const next = new Set(selectedRecipients);
                        if (next.has(primaryEmail)) next.delete(primaryEmail);
                        else next.add(primaryEmail);
                        setSelectedRecipients(next);
                      }}
                    />
                    <span>{primaryEmail}</span>
                    <span style={{ color: '#2563eb', fontSize: 12, fontWeight: 600 }}>Primary</span>
                  </label>
                )}
                <div style={{ marginTop: 8, fontSize: 13 }}>
                  <button
                    type="button"
                    className="btn-link"
                    style={{ color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginRight: 12 }}
                    onClick={() => {
                      const all = new Set(recipientOptions.map(u => u.email));
                      if (primaryEmail) all.add(primaryEmail);
                      setSelectedRecipients(all);
                    }}
                  >Select All</button>
                  <button
                    type="button"
                    className="btn-link"
                    style={{ color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    onClick={() => setSelectedRecipients(new Set())}
                  >Deselect All</button>
                </div>
              </div>
            )}
            {/* Per-invoice send choice — Sibyl decides each time. */}
            <div style={{ marginTop: 16, padding: '10px 12px', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '4px 0' }}>
                <input
                  type="radio"
                  name="send-mode"
                  checked={!sendWithTracking}
                  onChange={() => setSendWithTracking(false)}
                />
                <span>Send now — invoice only</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '4px 0' }}>
                <input
                  type="radio"
                  name="send-mode"
                  checked={sendWithTracking}
                  onChange={() => setSendWithTracking(true)}
                />
                <span>
                  Send with tracking
                  {invoice?.order && !(invoice.order.pro_number || (invoice.order.tracking_event_count || 0) > 0) && (
                    <span style={{ color: '#92400e', fontSize: 13 }}> (no tracking yet — order hasn't shipped)</span>
                  )}
                </span>
              </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <button className="btn btn-secondary" onClick={() => setShowSendModal(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleSendToRecipients()}
                disabled={selectedRecipients.size === 0 || sendingInvoice}
              >
                {sendingInvoice ? 'Sending...' : `Send to ${selectedRecipients.size} recipient${selectedRecipients.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BOL Create Modal — calls the SAME create-or-get endpoint the
          mill uses; if the mill already made one, we attach it. */}
      {showBolCreateModal && invoice?.order && (
        <div className="modal-overlay" onClick={() => setShowBolCreateModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 620 }}>
            <h3 style={{ marginBottom: 4 }}>
              {bolReplaceMode ? 'Re-quote & Replace BOL' : 'Create BOL'} — Order {invoice.order.order_number}
            </h3>
            <p style={{ color: '#6b7280', fontSize: 13, marginTop: 0 }}>
              {bolReplaceMode
                ? 'Voids the current BOL and creates a corrected one. Freight is priced per pallet — enter the re-quoted count/weight.'
                : 'The same BOL the mill crew pulls at shipment. Pallet count & weight are your quoted freight numbers.'}
            </p>

            {/* Field parity with the mill builder (MillShipping.tsx): two-col grid. */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label>Tracking/BOL # (shipper barcode) *</label>
                <input type="text" className="edit-input" value={bolTrackingNumber}
                  onChange={e => setBolTrackingNumber(e.target.value)} placeholder="Barcode sticker number..." />
              </div>
              <div className="form-group">
                <label>Quote #</label>
                <input type="text" className="edit-input" value={bolQuoteNumber}
                  onChange={e => setBolQuoteNumber(e.target.value)} placeholder="Freight quote number..." />
              </div>
              <div className="form-group">
                <label>Ship Date</label>
                <input type="date" className="edit-input" value={bolShipDate}
                  onChange={e => setBolShipDate(e.target.value)} />
              </div>
              <div className="form-group">
                <label>Carrier</label>
                <select className="edit-input" value={bolCarrier} onChange={e => setBolCarrier(e.target.value)}>
                  <option value="FASTWAY">Fastway Freight</option>
                  <option value="PENINSULA">Peninsula Truck Lines</option>
                  <option value="OAK_HARBOR">Oak Harbor Freight</option>
                  <option value="DIAMOND_LINE">Diamond Line Delivery</option>
                  <option value="UPS_FREIGHT">UPS Freight</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              {/* Sibyl's quoted freight numbers — authoritative, print on the BOL. */}
              <div className="form-group">
                <label>Number of Pallets (quoted)</label>
                <input type="text" inputMode="numeric" className="edit-input" value={bolPalletCount}
                  onChange={e => setBolPalletCount(e.target.value)} placeholder="e.g. 4" />
              </div>
              <div className="form-group">
                <label>Total Weight lbs (quoted)</label>
                <input type="text" inputMode="numeric" className="edit-input" value={bolWeight}
                  onChange={e => setBolWeight(e.target.value)} placeholder="e.g. 9400" />
              </div>
              <div className="form-group">
                <label>Driver Name</label>
                <input type="text" className="edit-input" value={bolDriverName}
                  onChange={e => setBolDriverName(e.target.value)} placeholder="Optional" />
              </div>
              <div className="form-group">
                <label>Trailer Number</label>
                <input type="text" className="edit-input" value={bolTrailerNumber}
                  onChange={e => setBolTrailerNumber(e.target.value)} placeholder="Optional" />
              </div>
              <div className="form-group">
                <label>Pickup # <span style={{ fontWeight: 400, color: '#888' }}>(carrier pickup confirmation)</span></label>
                <input type="text" className="edit-input" value={bolPickupNumber}
                  onChange={e => setBolPickupNumber(e.target.value)} placeholder="Optional — prints below the quote #" />
              </div>
            </div>

            {/* Special Services — these print as checkboxes on the BOL. Liftgate
                pre-fills from the order; all are overridable here. */}
            <div className="form-group">
              <label>Special Services</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1.25rem', marginTop: 4 }}>
                {([
                  ['Liftgate Required', bolLiftgate, setBolLiftgate],
                  ['Residential Delivery', bolResidential, setBolResidential],
                  ['Inside Delivery', bolInside, setBolInside],
                  ['Appointment Required', bolAppointment, setBolAppointment],
                  ['Call Before Delivery', bolNotify, setBolNotify],
                ] as [string, boolean, React.Dispatch<React.SetStateAction<boolean>>][]).map(([label, checked, setter]) => (
                  <label key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 400, cursor: 'pointer' }}>
                    <input type="checkbox" checked={checked} onChange={e => setter(e.target.checked)} />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            {/* Customer's order notes — shown for reference, NOT force-pushed.
                Sibyl chooses: use them, edit them, or leave the box blank. */}
            {invoice.order.special_instructions && (
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', padding: '0.6rem 0.75rem', borderRadius: 8, marginBottom: 8 }}>
                <div style={{ fontSize: 13, color: '#92400e' }}>
                  <strong>Customer's notes:</strong> {invoice.order.special_instructions}
                </div>
                <button type="button" className="bol-btn bol-btn-secondary" style={{ marginTop: 6, padding: '2px 10px', fontSize: 12 }}
                  onClick={() => setBolSpecialInstructions(invoice.order!.special_instructions)}>
                  Use these notes
                </button>
              </div>
            )}
            <div className="form-group">
              <label>Special Instructions <span style={{ fontWeight: 400, color: '#888' }}>(prints on the BOL)</span></label>
              <textarea className="edit-input" rows={2} value={bolSpecialInstructions}
                onChange={e => setBolSpecialInstructions(e.target.value)}
                placeholder="Blank, or click 'Use these notes' above, or type your own..." />
            </div>

            {/* Shipper Information block — matches the mill builder. */}
            <div style={{ background: '#f9fafb', padding: '0.75rem 1rem', borderRadius: 8, marginTop: 4 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Shipper Information</div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>
                Red Bridge Farm Feed Co.<br />145 W 3rd Ave<br />Kettle Falls, WA 99141<br />509-738-9004
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="bol-btn bol-btn-secondary" onClick={() => setShowBolCreateModal(false)} disabled={bolWorking}>
                Cancel
              </button>
              <button className="bol-btn bol-btn-primary" onClick={handleCreateBol}
                disabled={!bolTrackingNumber.trim() || bolWorking}>
                {bolWorking ? 'Saving...' : bolReplaceMode ? 'Replace BOL' : 'Create BOL'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Record Payment</h2>
            <div className="modal-body">
              <div className="form-group">
                <label>Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder={invoice.balance_due}
                />
              </div>
              <div className="form-group">
                <label>Payment Method</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                >
                  <option value="check">Check</option>
                  <option value="cash">Cash</option>
                  <option value="credit_card">Credit Card</option>
                  <option value="ach">ACH Transfer</option>
                  <option value="wire">Wire Transfer</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Reference Number / Note (optional)</label>
                <textarea
                  rows={3}
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  placeholder="Check #, Transaction ID, or a longer note (e.g. which QB invoices this covers)"
                  style={{ resize: 'vertical', width: '100%' }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowPaymentModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleRecordPayment}
                disabled={!paymentAmount || actionLoading}
              >
                {actionLoading ? 'Recording...' : 'Record Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Void single payment modal */}
      {voidingPayment && invoice && (
        <div className="modal-overlay" onClick={() => !voidLoading && setVoidingPayment(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Void this payment of {formatCurrency(voidingPayment.amount)}?</h2>
            <div className="modal-body">
              <p>This will:</p>
              <ul style={{ marginLeft: '1.25rem', marginTop: 0 }}>
                <li>Reverse the payment in QuickBooks</li>
                <li>Recalculate the invoice balance</li>
                <li>Mark the invoice as outstanding if balance &gt; 0</li>
              </ul>
              {invoice.is_deleted && (
                <div className="message warning" style={{ marginTop: '0.75rem' }}>
                  This invoice is currently deleted. Voiding the payment will still
                  update QuickBooks. Continue?
                </div>
              )}
              <div className="form-group" style={{ marginTop: '0.75rem' }}>
                <label htmlFor="void-reason">Reason (optional)</label>
                <textarea
                  id="void-reason"
                  value={voidReason}
                  onChange={(e) => setVoidReason(e.target.value)}
                  placeholder="e.g. bounced check, duplicate entry, customer disputed"
                  rows={3}
                />
              </div>
              <div className="form-group" style={{ marginTop: '0.5rem' }}>
                <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={voidConfirmChecked}
                    onChange={(e) => setVoidConfirmChecked(e.target.checked)}
                  />
                  I understand this affects QuickBooks records.
                </label>
              </div>
              {voidError && <div className="message error">{voidError}</div>}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setVoidingPayment(null)}
                disabled={voidLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                style={{ backgroundColor: '#dc2626', borderColor: '#dc2626' }}
                onClick={handleVoidPayment}
                disabled={voidLoading || !voidConfirmChecked}
              >
                {voidLoading ? 'Voiding…' : 'Void Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Unmark Paid modal */}
      {showUnmarkPaidModal && invoice && (
        <div className="modal-overlay" onClick={() => !unmarkLoading && setShowUnmarkPaidModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Unmark this invoice as paid?</h2>
            <div className="modal-body">
              {(() => {
                const active = (invoice.payments || []).filter(
                  (p) => !p.voided && parseFloat(p.amount) > 0
                );
                const total = active.reduce((s, p) => s + parseFloat(p.amount), 0);
                return (
                  <>
                    <p>
                      This will void all <strong>{active.length}</strong> active
                      payment(s) on this invoice and reverse them in QuickBooks:
                    </p>
                    <ul style={{ marginLeft: '1.25rem' }}>
                      {active.map((p) => (
                        <li key={p.id}>
                          {formatCurrency(p.amount)} via {p.payment_method || 'unknown'} on {formatDate(p.payment_date)}
                          {p.reference_number && ` (${p.reference_number})`}
                        </li>
                      ))}
                    </ul>
                    <p>
                      <strong>Total to be voided:</strong> {formatCurrency(total.toFixed(2))}
                    </p>
                  </>
                );
              })()}
              <div className="form-group" style={{ marginTop: '0.75rem' }}>
                <label htmlFor="unmark-reason">Reason (optional)</label>
                <textarea
                  id="unmark-reason"
                  value={unmarkReason}
                  onChange={(e) => setUnmarkReason(e.target.value)}
                  placeholder="e.g. invoice was marked paid in error, customer payment never landed"
                  rows={3}
                />
              </div>
              <div className="form-group" style={{ marginTop: '0.5rem' }}>
                <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={unmarkConfirmChecked}
                    onChange={(e) => setUnmarkConfirmChecked(e.target.checked)}
                  />
                  I understand this affects QuickBooks records.
                </label>
              </div>
              {unmarkError && <div className="message error">{unmarkError}</div>}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowUnmarkPaidModal(false)}
                disabled={unmarkLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                style={{ backgroundColor: '#92400e', borderColor: '#92400e' }}
                onClick={handleUnmarkPaid}
                disabled={unmarkLoading || !unmarkConfirmChecked}
              >
                {unmarkLoading ? 'Voiding…' : 'Unmark Paid'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteModal && invoice && (
        <div className="modal-overlay" onClick={() => !deletingInvoice && setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Delete invoice {invoice.invoice_number || `#${invoice.id}`}?</h2>
            <div className="modal-body">
              <p>
                The invoice will be hidden from all lists and the customer
                portal. The invoice number stays reserved in the database
                and will never be reused.
              </p>
              <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                Tip: a reason helps later when reconciling.
              </p>
              <div className="form-group">
                <label htmlFor="delete-reason">Reason (optional)</label>
                <textarea
                  id="delete-reason"
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="e.g. duplicate of INV-20260507-003, customer cancelled, etc."
                  rows={3}
                />
              </div>
              {deleteError && (
                <div className="message error">{deleteError}</div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={deletingInvoice}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                style={{ backgroundColor: '#dc2626', borderColor: '#dc2626' }}
                onClick={handleDeleteInvoice}
                disabled={deletingInvoice}
              >
                {deletingInvoice ? 'Deleting…' : 'Delete Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Printable Invoice View */}
      {showPrintView && fullInvoiceData && (
        <div className="print-modal-overlay">
          <PrintableInvoice
            data={fullInvoiceData}
            onClose={() => {
              setShowPrintView(false);
              setFullInvoiceData(null);
            }}
          />
        </div>
      )}

      {/* Printable Packing Slip View */}
      {showPackingSlip && packingSlipData && (
        <div className="print-modal-overlay">
          <PrintablePackingSlip
            data={packingSlipData}
            onClose={() => {
              setShowPackingSlip(false);
              setPackingSlipData(null);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default InvoiceDetail;
